// Federal RCRA hazardous waste codes (D/F/K/P/U lists, plus a few RCRAInfo-specific
// administrative codes: LABP "Lab Pack", PHARMS/PHRM "Hazardous Waste Pharmaceuticals") and their
// official descriptions, as published by EPA/RCRAInfo. Static reference data - like
// dot-id-numbers.ts, this changes only rarely (EPA occasionally adds new listed-waste codes), so
// it's stored as a plain array rather than fetched from a live lookup service.
export interface EpaWasteCode {
  code: string;
  description: string;
}

export interface EpaWasteCodeIndicator {
  label: 'Ignitable' | 'Corrosive' | 'Reactive' | 'Toxic' | 'Listed';
  toxicityThresholdMgL?: number;
}

export interface EpaWasteCodeRegulatoryDefinition {
  citation: string;
  definition: string;
  url: string;
}

// 40 CFR 261.24, Table 1 maximum concentrations for the toxicity characteristic.
const TOXICITY_THRESHOLDS_MG_L: Readonly<Record<string, number>> = {
  D004: 5,
  D005: 100,
  D006: 1,
  D007: 5,
  D008: 5,
  D009: 0.2,
  D010: 1,
  D011: 5,
  D012: 0.02,
  D013: 0.4,
  D014: 10,
  D015: 0.5,
  D016: 10,
  D017: 1,
  D018: 0.5,
  D019: 0.5,
  D020: 0.03,
  D021: 100,
  D022: 6,
  D023: 200,
  D024: 200,
  D025: 200,
  D026: 200,
  D027: 7.5,
  D028: 0.5,
  D029: 0.7,
  D030: 0.13,
  D031: 0.008,
  D032: 0.13,
  D033: 0.5,
  D034: 0.2,
  D035: 200,
  D036: 2,
  D037: 100,
  D038: 5,
  D039: 0.7,
  D040: 0.5,
  D041: 400,
  D042: 2,
  D043: 0.2,
};

export function epaWasteCodeIndicator(code: string): EpaWasteCodeIndicator | null {
  if (code === 'D001') return { label: 'Ignitable' };
  if (code === 'D002') return { label: 'Corrosive' };
  if (code === 'D003') return { label: 'Reactive' };

  const toxicityThresholdMgL = TOXICITY_THRESHOLDS_MG_L[code];
  if (toxicityThresholdMgL !== undefined) return { label: 'Toxic', toxicityThresholdMgL };

  if (/^[FKPU]/.test(code)) return { label: 'Listed' };
  return null;
}

export function epaWasteCodeRegulatoryDefinition(code: string): EpaWasteCodeRegulatoryDefinition | null {
  if (code === 'D001') {
    return {
      citation: '40 CFR 261.21',
      definition:
        'A liquid waste is ignitable if it has a flash point below 60 C (140 F). Other qualifying wastes can cause fire through friction or moisture absorption, are ignitable compressed gases, or are oxidizers.',
      url: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-261/subpart-C/section-261.21',
    };
  }
  if (code === 'D002') {
    return {
      citation: '40 CFR 261.22',
      definition:
        'A waste is corrosive if it is aqueous with pH at or below 2 or at or above 12.5, or if it corrodes steel at more than 6.35 mm per year at 55 C.',
      url: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-261/subpart-C/section-261.22',
    };
  }
  if (code === 'D003') {
    return {
      citation: '40 CFR 261.23',
      definition:
        'A waste is reactive if it is unstable, reacts violently or with water, generates dangerous gases, or can detonate or explode under specified conditions.',
      url: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-261/subpart-C/section-261.23',
    };
  }
  if (TOXICITY_THRESHOLDS_MG_L[code] !== undefined) {
    return {
      citation: '40 CFR 261.24',
      definition:
        'A waste is toxic if its TCLP extract contains a listed contaminant at or above the Table 1 maximum concentration.',
      url: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-261/subpart-C/section-261.24',
    };
  }
  if (code.startsWith('F')) {
    return {
      citation: '40 CFR 261.31',
      definition:
        'F-listed wastes are hazardous wastes from nonspecific sources, including the specified spent solvents and wastewaters described in this section.',
      url: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-261/subpart-D/section-261.31',
    };
  }
  if (code.startsWith('K')) {
    return {
      citation: '40 CFR 261.32',
      definition:
        'K-listed wastes are hazardous wastes from the source-specific industrial processes identified in this section.',
      url: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-261/subpart-D/section-261.32',
    };
  }
  if (code.startsWith('P')) {
    return {
      citation: '40 CFR 261.33(e)',
      definition:
        'P-listed wastes are discarded commercial chemical products that are acutely hazardous when discarded.',
      url: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-261/subpart-D/section-261.33',
    };
  }
  if (code.startsWith('U')) {
    return {
      citation: '40 CFR 261.33(f)',
      definition:
        'U-listed wastes are discarded commercial chemical products that are hazardous when discarded but are not acutely hazardous P-listed wastes.',
      url: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-261/subpart-D/section-261.33',
    };
  }
  return null;
}

export function epaWasteCodeDisplayDescription(wasteCode: EpaWasteCode): string {
  return wasteCode.description
    .toLowerCase()
    .replace(/(^|[\s([,/-])([a-z])/g, (_, prefix: string, letter: string) => `${prefix}${letter.toUpperCase()}`);
}

export const EPA_WASTE_CODES: readonly EpaWasteCode[] = [
  {
    code: 'D001',
    description: 'IGNITABLE WASTE',
  },
  {
    code: 'D002',
    description: 'CORROSIVE WASTE',
  },
  {
    code: 'D003',
    description: 'REACTIVE WASTE',
  },
  {
    code: 'D004',
    description: 'ARSENIC',
  },
  {
    code: 'D005',
    description: 'BARIUM',
  },
  {
    code: 'D006',
    description: 'CADMIUM',
  },
  {
    code: 'D007',
    description: 'CHROMIUM',
  },
  {
    code: 'D008',
    description: 'LEAD',
  },
  {
    code: 'D009',
    description: 'MERCURY',
  },
  {
    code: 'D010',
    description: 'SELENIUM',
  },
  {
    code: 'D011',
    description: 'SILVER',
  },
  {
    code: 'D012',
    description:
      'ENDRIN (1,2,3,4,10,10-HEXACHLORO-1,7-EPOXY-1,4,4A,5,6,7,8,8A-OCTAHYDRO-1,4-ENDO, ENDO-5,8-DIMETH-ANO-NAPHTHALENE)',
  },
  {
    code: 'D013',
    description: 'LINDANE (1,2,3,4,5,6-HEXA-CHLOROCYCLOHEXANE, GAMMA ISOMER)',
  },
  {
    code: 'D014',
    description: 'METHOXYCHLOR (1,1,1-TRICHLORO-2,2-BIS [P-METHOXYPHENYL] ETHANE)',
  },
  {
    code: 'D015',
    description: 'TOXAPHENE (C10 H10 CL8, TECHNICAL CHLORINATED CAMPHENE, 67-69 PERCENT CHLORINE)',
  },
  {
    code: 'D016',
    description: '2,4-D (2,4-DICHLOROPHENOXYACETIC ACID)',
  },
  {
    code: 'D017',
    description: '2,4,5-TP SILVEX (2,4,5-TRICHLOROPHENOXYPROPIONIC ACID)',
  },
  {
    code: 'D018',
    description: 'BENZENE',
  },
  {
    code: 'D019',
    description: 'CARBON TETRACHLORIDE',
  },
  {
    code: 'D020',
    description: 'CHLORDANE',
  },
  {
    code: 'D021',
    description: 'CHLOROBENZENE',
  },
  {
    code: 'D022',
    description: 'CHLOROFORM',
  },
  {
    code: 'D023',
    description: 'O-CRESOL',
  },
  {
    code: 'D024',
    description: 'M-CRESOL',
  },
  {
    code: 'D025',
    description: 'P-CRESOL',
  },
  {
    code: 'D026',
    description: 'CRESOL',
  },
  {
    code: 'D027',
    description: '1,4-DICHLOROBENZENE',
  },
  {
    code: 'D028',
    description: '1,2-DICHLOROETHANE',
  },
  {
    code: 'D029',
    description: '1,1-DICHLOROETHYLENE',
  },
  {
    code: 'D030',
    description: '2,4-DINITROTOLUENE',
  },
  {
    code: 'D031',
    description: 'HEPTACHLOR (AND ITS EPOXIDE)',
  },
  {
    code: 'D032',
    description: 'HEXACHLOROBENZENE',
  },
  {
    code: 'D033',
    description: 'HEXACHLOROBUTADIENE',
  },
  {
    code: 'D034',
    description: 'HEXACHLOROETHANE',
  },
  {
    code: 'D035',
    description: 'METHYL ETHYL KETONE',
  },
  {
    code: 'D036',
    description: 'NITROBENZENE',
  },
  {
    code: 'D037',
    description: 'PENTACHLOROPHENOL',
  },
  {
    code: 'D038',
    description: 'PYRIDINE',
  },
  {
    code: 'D039',
    description: 'TETRACHLOROETHYLENE',
  },
  {
    code: 'D040',
    description: 'TRICHLOROETHYLENE',
  },
  {
    code: 'D041',
    description: '2,4,5-TRICHLOROPHENOL',
  },
  {
    code: 'D042',
    description: '2,4,6-TRICHLOROPHENOL',
  },
  {
    code: 'D043',
    description: 'VINYL CHLORIDE',
  },
  {
    code: 'F001',
    description:
      'THE FOLLOWING SPENT HALOGENATED SOLVENTS USED IN DEGREASING: TETRACHLOROETHYLENE, TRICHLORETHYLENE, METHYLENE CHLORIDE, 1,1,1-TRICHLOROETHANE, CARBON TETRACHLORIDE AND CHLORINATED FLUOROCARBONS; ALL SPENT SOLVENT MIXTURES/BLENDS USED IN DEGREASING CONTAINING, BEFORE USE, A TOTAL OF TEN PERCENT OR MORE (BY VOLUME) OF ONE OR MORE OF THE ABOVE HALOGENATED SOLVENTS OR THOSE SOLVENTS LISTED IN F002, F004, AND F005; AND STILL BOTTOMS FROM THE RECOVERY OF THESE SPENT SOLVENTS AND SPENT SOLVENT MIXTURES.',
  },
  {
    code: 'F002',
    description:
      'THE FOLLOWING SPENT HALOGENATED SOLVENTS: TETRACHLOROETHYLENE, METHYLENE CHLORIDE, TRICHLOROETHYLENE, 1,1,1-TRICHLOROETHANE, CHLOROBENZENE, 1,1,2-TRICHLORO-1,2,2-TRIFLUOROETHANE, ORTHO-DICHLOROBENZENE, TRICHLOROFLUOROMETHANE, AND 1,1,2, TRICHLOROETHANE; ALL SPENT SOLVENT MIXTURES/BLENDS CONTAINING, BEFORE USE, A TOTAL OF TEN PERCENT OR MORE (BY VOLUME) OF ONE OR MORE OF THE ABOVE HALOGENATED SOLVENTS OR THOSE SOLVENTS LISTED IN F001, F004, AND F005; AND STILL BOTTOMS FROM THE RECOVERY OF THESE SPENT SOLVENTS AND SPENT SOLVENT MIXTURES.',
  },
  {
    code: 'F003',
    description:
      'THE FOLLOWING SPENT NONHALOGENATED SOLVENTS: XYLENE, ACETONE, ETHYL ACETATE, ETHYL BENZENE, ETHYL ETHER, METHYL ISOBUTYL KETONE, N-BUTYL ALCOHOL, CYCLOHEXANONE, AND METHANOL; ALL SPENT SOLVENT MIXTURES/BLENDS CONTAINING, BEFORE USE, ONLY THE ABOVE SPENT NONHALOGENATED SOLVENTS; AND ALL SPENT SOLVENT MIXTURES/BLENDS CONTAINING, BEFORE USE, ONE OR MORE OF THE ABOVE NONHALOGENATED SOLVENTS, AND A TOTAL OF TEN PERCENT OR MORE (BY VOLUME) OF ONE OR MORE OF THOSE SOLVENTS LISTED IN F001, F002, F004, AND F005; AND STILL BOTTOMS FROM THE RECOVERY OF THESE SPENT SOLVENTS AND SPENT SOLVENT MIXTURES.',
  },
  {
    code: 'F004',
    description:
      'THE FOLLOWING SPENT NONHALOGENATED SOLVENTS: CRESOLS, CRESYLIC ACID, AND NITROBENZENE; AND THE STILL BOTTOMS FROM THE RECOVERY OF THESE SOLVENTS; ALL SPENT SOLVENT MIXTURES/BLENDS CONTAINING, BEFORE USE, A TOTAL OF TEN PERCENT OR MORE (BY VOLUME) OF ONE OR MORE OF THE ABOVE NONHALOGENATED SOLVENTS OR THOSE SOLVENTS LISTED IN F001, F002, AND F005; AND STILL BOTTOMS FROM THE RECOVERY OF THESE SPENT SOLVENTS AND SPENT SOLVENT MIXTURES.',
  },
  {
    code: 'F005',
    description:
      'THE FOLLOWING SPENT NONHALOGENATED SOLVENTS: TOLUENE, METHYL ETHYL KETONE, CARBON DISULFIDE, ISOBUTANOL, PYRIDINE, BENZENE, 2-ETHOXYETHANOL, AND 2-NITROPROPANE; ALL SPENT SOLVENT MIXTURES/BLENDS CONTAINING, BEFORE USE, A TOTAL OF TEN PERCENT OR MORE (BY VOLUME) OF ONE OR MORE OF THE ABOVE NONHALOGENATED SOLVENTS OR THOSE SOLVENTS LISTED IN F001, F002, OR F004; AND STILL BOTTOMS FROM THE RECOVERY OF THESE SPENT SOLVENTS AND SPENT SOLVENT MIXTURES.',
  },
  {
    code: 'F006',
    description:
      'WASTEWATER TREATMENT SLUDGES FROM ELECTROPLATING OPERATIONS, EXCEPT FROM THE FOLLOWING PROCESSES: (1) SULFURIC ACID ANODIZING OF ALUMINUM; (2) TIN PLATING ON CARBON STEEL; (3) ZINC PLATING (SEGREGATED BASIS) ON CARBON STEEL; (4) ALUMINUM OR ZINC-ALUMINUM PLATING ON CARBON STEEL; (5) CLEANING/STRIPPING ASSOCIATED WITH TIN, ZINC, AND ALUMINUM PLATING ON CARBON STEEL; AND (6) CHEMICAL ETCHING AND MILLING OF ALUMINUM.',
  },
  {
    code: 'F007',
    description: 'SPENT CYANIDE PLATING BATH SOLUTIONS FROM ELECTROPLATING OPERATIONS.',
  },
  {
    code: 'F008',
    description:
      'PLATING BATH RESIDUES FROM THE BOTTOM OF PLATING BATHS FROM ELECTROPLATING OPERATIONS IN WHICH CYANIDES ARE USED IN THE PROCESS.',
  },
  {
    code: 'F009',
    description:
      'SPENT STRIPPING AND CLEANING BATH SOLUTIONS FROM ELECTROPLATING OPERATIONS IN WHICH CYANIDES ARE USED IN THE PROCESS.',
  },
  {
    code: 'F010',
    description:
      'QUENCHING BATH RESIDUES FROM OIL BATHS FROM METAL HEAT TREATING OPERATIONS IN WHICH CYANIDES ARE USED IN THE PROCESS.',
  },
  {
    code: 'F011',
    description: 'SPENT CYANIDE SOLUTIONS FROM SLAT BATH POT CLEANING FROM METAL HEAT TREATING OPERATIONS.',
  },
  {
    code: 'F012',
    description:
      'QUENCHING WASTEWATER TREATMENT SLUDGES FROM METAL HEAT TREATING OPERATIONS IN WHICH CYANIDES ARE USED IN THE PROCESS.',
  },
  {
    code: 'F019',
    description:
      'WASTEWATER TREATMENT SLUDGES FROM THE CHEMICAL CONVERSION COATING OF ALUMINUM, EXCEPT FROM ZIRCONIUM PHOSPHATING IN ALUMINUM CAN WASHING WHEN SUCH PHOSPHATING IS AN EXCLUSIVE CONVERSION COATING PROCESS.',
  },
  {
    code: 'F020',
    description:
      'WASTES (EXCEPT WASTEWATER AND SPENT CARBON FROM HYDROGEN CHLORIDE PURIFICATION) FROM THE PRODUCTION OR MANUFACTURING USE (AS A REACTANT, CHEMICAL INTERMEDIATE, OR COMPONENT IN A FORMULATING PROCESS) OF TRI- OR TETRACHLOROPHENOL OR OF INTERMEDIATES USED TO PRODUCE THEIR PESTICIDE DERIVATIVES.  (THIS LISTING DOES NOT INCLUDE WASTES FROM THE PRODUCTION OF HEXACHLOROPHENE FROM HIGHLY PURIFIED 2,4,5-TRICHLOROPHENOL.)',
  },
  {
    code: 'F021',
    description:
      'WASTES (EXCEPT WASTEWATER AND SPENT CARBON FROM HYDROGEN CHLORIDE PURIFICATION) FROM THE PRODUCTION OR MANUFACTURING USE (AS A REACTANT, CHEMICAL INTERMEDIATE, OR COMPONENT IN A FORMULATING PROCESS) OF PENTACHLOROPHENOL, OR OF INTERMEDIATES USED TO PRODUCE DERIVATIVES.',
  },
  {
    code: 'F022',
    description:
      'WASTES (EXCEPT WASTEWATER AND SPENT CARBON FROM HYDROGEN CHLORIDE PURIFICATION) FROM THE MANUFACTURING USE (AS A REACTANT, CHEMICAL INTERMEDIATE, OR COMPONENT IN A FORMULATING PROCESS) OF TETRA-, PENTA-, OR HEXACHLOROBENZENES UNDER ALKALINE CONDITIONS.',
  },
  {
    code: 'F023',
    description:
      'WASTES (EXCEPT WASTEWATER AND SPENT CARBON FROM HYDROGEN CHLORIDE PURIFICATION) FROM THE PRODUCTION OF MATERIALS ON EQUIPMENT PREVIOUSLY USED FOR THE PRODUCTION OR MANUFACTURING USE (AS A REACTANT, CHEMICAL INTERMEDIATE, OR COMPONENT IN A FORMULATING PROCESS) OF TRI- AND TETRACHLOROPHENOLS.  (THIS LISTING DOES NOT INCLUDE WASTES FROM EQUIPMENT USED ONLY FOR THE PRODUCTION OR USE OF HEXACHLOROPHENE FROM HIGHLY PURIFIED 2,4,5-TRICHLOROPHENOL.)',
  },
  {
    code: 'F024',
    description:
      'PROCESS WASTES INCLUDING, BUT NOT LIMITED TO, DISTILLATION RESIDUES, HEAVY ENDS, TARS, AND REACTOR CLEAN-OUT WASTES FROM THE PRODUCTION OF CERTAIN CHLORINATED ALIPHATIC HYDROCARBONS BY FREE RADICAL CATALYZED PROCESSES.  THESE CHLORINATED ALIPHATIC HYDROCARBONS ARE THOSE HAVING CARBON CHAIN LENGTHS RANGING FROM ONE TO, AND INCLUDING FIVE, WITH VARYING AMOUNTS AND POSITIONS OF CHLORINE SUBSTITUTION.  (THIS LISTING DOES NOT INCLUDE WASTEWATERS, WASTEWATER TREATMENT SLUDGE, SPENT CATALYSTS, AND WASTES LISTED IN SECTIONS 261.31. OR 261.32)',
  },
  {
    code: 'F025',
    description:
      'CONDENSED LIGHT ENDS, SPENT FILTERS AND FILTER AIDS, AND SPENT DESICCANT WASTES FROM THE PRODUCTION OF CERTAIN CHLORINATED ALIPHATIC HYDROCARBONS BY FREE RADICAL CATALYZED PROCESSES.  THESE CHLORINATED ALIPHATIC HYDROCARBONS ARE THOSE HAVING CARBON CHAIN LENGTHS RANGING FROM ONE TO, AND INCLUDING FIVE, WITH VARYING AMOUNTS AND POSITIONS OF CHLORINE SUBSTITUTION.',
  },
  {
    code: 'F026',
    description:
      'WASTES (EXCEPT WASTEWATER AND SPENT CARBON FROM HYDROGEN CHLORIDE PURIFICATION) FROM THE PRODUCTION OF MATERIALS ON EQUIPMENT PREVIOUSLY USED FOR THE MANUFACTURING USE (AS A REACTANT, CHEMICAL INTERMEDIATE, OR COMPONENT IN A FORMULATING PROCESS) OF TETRA-, PENTA-, OR HEXACHLOROBENZENE UNDER ALKALINE CONDITIONS.',
  },
  {
    code: 'F027',
    description:
      'DISCARDED UNUSED FORMULATIONS CONTAINING TRI-, TETRA-, OR PENTACHLOROPHENOL OR DISCARDED UNUSED FORMULATIONS CONTAINING COMPOUNDS DERIVED FROM THESE CHLOROPHENOLS.  (THIS LISTING DOES NOT INCLUDE FORMULATIONS CONTAINING HEXACHLOROPHENE SYNTHESIZED FROM PREPURIFIED 2,4,5-TRICHLOROPHENOL AS THE SOLE COMPONENT.)',
  },
  {
    code: 'F028',
    description:
      'RESIDUES RESULTING FROM THE INCINERATION OR THERMAL TREATMENT OF SOIL CONTAMINATED WITH EPA HAZARDOUS WASTE NOS. F020, F021, F022, F023, F026, AND F027.',
  },
  {
    code: 'F032',
    description:
      'WASTEWATERS, PROCESS RESIDUALS, PRESERVATIVE DRIPPAGE, AND SPENT FORMULATIONS FROM WOOD PRESERVING PROCESSES GENERATED AT PLANTS THAT CURRENTLY USE, OR HAVE PREVIOUSLY USED, CHLOROPHENOLIC FORMULATIONS [EXCEPT POTENTIALLY CROSS-CONTAMINATED WASTES THAT HAVE HAD THE F032 WASTE CODE DELETED IN ACCORDANCE WITH SECTION 261.35 (I.E., THE NEWLY PROMULGATED EQUIPMENT CLEANING OR REPLACEMENT STANDARDS), AND WHERE THE GENERATOR DOES NOT RESUME OR INITIATE USE OF CHLOROPHENOLIC FORMULATIONS].  (THIS LISTING DOES NOT INCLUDE K001 BOTTOM SEDIMENT SLUDGE FROM THE TREATMENT OF WASTEWATER FROM WOOD PRESERVING PROCESSES THAT USE CREOSOTE AND/OR PENTACHLOROPHENOL.)',
  },
  {
    code: 'F034',
    description:
      'WASTEWATERS, PROCESS RESIDUALS, PRESERVATIVE DRIPPAGE, AND SPENT FORMULATIONS FROM WOOD PRESERVING PROCESSES GENERATED AT PLANTS THAT USE CREOSOTE FORMULATIONS.  THIS LISTING DOES NOT INCLUDE K001 BOTTOM SEDIMENT SLUDGE FROM THE TREATMENT OF WASTEWATER FROM WOOD PRESERVING PROCESSES THAT USE CREOSOTE AND/OR PENTACHLOROPHENOL.',
  },
  {
    code: 'F035',
    description:
      'WASTEWATERS, PROCESS RESIDUALS, PRESERVATIVE DRIPPAGE, AND SPENT FORMULATIONS FROM WOOD PRESERVING PROCESSES GENERATED AT PLANTS THAT USE INORGANIC PRESERVATIVES CONTAINING ARSENIC OR CHROMIUM.  THIS LISTING DOES NOT INCLUDE K001 BOTTOM SEDIMENT SLUDGE FROM THE TREATMENT OF WASTEWATER FROM WOOD PRESERVING PROCESSES THAT USE CREOSOTE AND/OR PENTACHLOROPHENOL.',
  },
  {
    code: 'F037',
    description:
      'PETROLEUM REFINERY PRIMARY OIL/WATER/SOLIDS SEPARATION SLUDGE - ANY SLUDGE GENERATED FROM THE GRAVITATIONAL SEPARATION OF OIL/WATER/SOLIDS DURING THE STORAGE OR TREATMENT OF PROCESS WASTEWATERS AND OILY COOLING WASTEWATERS FROM PETROLEUM REFINERIES.  SUCH SLUDGES INCLUDE, BUT ARE NOT LIMITED TO, THOSE GENERATED IN OIL/WATER/SOLIDS SEPARATORS; TANKS AND IMPOUNDMENTS; DITCHES AND OTHER CONVEYANCES; SUMPS; AND STORM WATER UNITS RECEIVING DRY WEATHER FLOW.  SLUDGES GENERATED IN STORM WATER UNITS THAT DO NOT RECEIVE DRY WEATHER FLOW, SLUDGES GENERATED IN AGGRESSIVE BIOLOGICAL TREATMENT UNITS AS DEFINED IN SECTION 261.31(B)(2) (INCLUDING SLUDGES GENERATED IN ONE OR MORE ADDITIONAL UNITS AFTER WASTEWATERS HAVE BEEN TREATED IN AGGRESSIVE BIOLOGICAL TREATMENT UNITS), AND K051 WASTES ARE EXEMPTED FROM THIS LISTING.',
  },
  {
    code: 'F038',
    description:
      'PETROLEUM REFINERY SECONDARY (EMULSIFIED) OIL/WATER/SOLIDS SEPARATION SLUDGE - ANY SLUDGE AND/OR FLOAT GENERATED FROM THE PHYSICAL AND/OR CHEMICAL SEPARATION OF OIL/WATER/SOLIDS IN PROCESS WASTEWATERS AND OILY COOLING WASTEWATERS FROM PETROLEUM REFINERIES.  SUCH WASTES INCLUDE, BUT ARE NOT LIMITED TO, ALL SLUDGES AND FLOATS GENERATED IN INDUCED AIR FLOTATION (IAF) UNITS, TANKS AND IMPOUNDMENTS, AND ALL SLUDGES GENERATED IN DAF UNITS.  SLUDGES GENERATED IN STORMWATER UNITS THAT DO NOT RECEIVE DRY WEATHER FLOW, SLUDGES GENERATED IN AGGRESSIVE BIOLOGICAL TREATMENT UNITS AS DEFINED IN SECTION 261.31(B)(2) (INCLUDING SLUDGES GENERATED IN ONE OR MORE ADDITIONAL UNITS AFTER WASTEWATERS HAVE BEEN TREATED IN AGGRESSIVE BIOLOGICAL TREATMENT UNITS), AND F037, K048, AND K051 WASTES ARE EXEMPTED FROM THIS LISTING.',
  },
  {
    code: 'F039',
    description:
      'LEACHATE RESULTING FROM THE TREATMENT, STORAGE, OR DISPOSAL OF WASTES CLASSIFIED BY MORE THAN ONE WASTE CODE UNDER SUBPART D, OR FROM A MIXTURE OF WASTES CLASSIFIED UNDER SUBPARTS C AND D OF THIS PART.  (LEACHATE RESULTING FROM THE MANAGEMENT OF ONE OR MORE OF THE FOLLOWING EPA HAZARDOUS WASTES AND NO OTHER HAZARDOUS WASTES RETAINS ITS HAZARDOUS WASTE CODE(S): F020, F021, F022, F023, F026, F027, AND/OR F028.)',
  },
  {
    code: 'K001',
    description:
      'BOTTOM SEDIMENT SLUDGE FROM THE TREATMENT OF WASTEWATERS FROM WOOD PRESERVING PROCESSES THAT USE CREOSOTE AND/OR PENTACHLOROPHENOL.',
  },
  {
    code: 'K002',
    description: 'WASTEWATER TREATMENT SLUDGE FROM THE PRODUCTION OF CHROME YELLOW AND ORANGE PIGMENTS.',
  },
  {
    code: 'K003',
    description: 'WASTEWATER TREATMENT SLUDGE FROM THE PRODUCTION OF MOLYBDATE ORANGE PIGMENTS.',
  },
  {
    code: 'K004',
    description: 'WASTEWATER TREATMENT SLUDGE FROM THE PRODUCTION OF ZINC YELLOW PIGMENTS.',
  },
  {
    code: 'K005',
    description: 'WASTEWATER TREATMENT SLUDGE FROM THE PRODUCTION OF CHROME GREEN PIGMENTS.',
  },
  {
    code: 'K006',
    description:
      'WASTEWATER TREATMENT SLUDGE FROM THE PRODUCTION OF CHROME OXIDE GREEN PIGMENTS (ANHYDROUS AND HYDRATED).',
  },
  {
    code: 'K007',
    description: 'WASTEWATER TREATMENT SLUDGE FROM THE PRODUCTION OF IRON BLUE PIGMENTS.',
  },
  {
    code: 'K008',
    description: 'OVEN RESIDUE FROM THE PRODUCTION OF CHROME OXIDE GREEN PIGMENTS.',
  },
  {
    code: 'K009',
    description: 'DISTILLATION BOTTOMS FROM THE PRODUCTION OF ACETALDEHYDE FROM ETHYLENE.',
  },
  {
    code: 'K010',
    description: 'DISTILLATION SIDE CUTS FROM THE PRODUCTION OF ACETALDEHYDE FROM ETHYLENE.',
  },
  {
    code: 'K011',
    description: 'BOTTOM STREAM FROM THE WASTEWATER STRIPPER IN THE PRODUCTION OF ACRYLONITRILE.',
  },
  {
    code: 'K013',
    description: 'BOTTOM STREAM FROM THE ACETONITRILE COLUMN IN THE PRODUCTION OF ACRYLONITRILE.',
  },
  {
    code: 'K014',
    description: 'BOTTOMS FROM THE ACETONITRILE PURIFICATION COLUMN IN THE PRODUCTION OF ACRYLONITRILE.',
  },
  {
    code: 'K015',
    description: 'STILL BOTTOMS FROM THE DISTILLATION OF BENZYL CHLORIDE.',
  },
  {
    code: 'K016',
    description: 'HEAVY ENDS OR DISTILLATION RESIDUES FROM THE PRODUCTION OF CARBON TETRACHLORIDE.',
  },
  {
    code: 'K017',
    description: 'HEAVY ENDS (STILL BOTTOMS) FROM THE PURIFICATION COLUMN IN THE PRODUCTION OF EPICHLOROHYDRIN.',
  },
  {
    code: 'K018',
    description: 'HEAVY ENDS FROM THE FRACTIONATION COLUMN IN ETHYL CHLORIDE PRODUCTION.',
  },
  {
    code: 'K019',
    description: 'HEAVY ENDS FROM THE DISTILLATION OF ETHYLENE DICHLORIDE IN ETHYLENE DICHLORIDE PRODUCTION.',
  },
  {
    code: 'K020',
    description: 'HEAVY ENDS FROM THE DISTILLATION OF VINYL CHLORIDE IN VINYL CHLORIDE MONOMER PRODUCTION.',
  },
  {
    code: 'K021',
    description: 'AQUEOUS SPENT ANTIMONY CATALYST WASTE FROM FLUOROMETHANE PRODUCTION.',
  },
  {
    code: 'K022',
    description: 'DISTILLATION BOTTOM TARS FROM THE PRODUCTION OF PHENOL/ACETONE FROM CUMENE.',
  },
  {
    code: 'K023',
    description: 'DISTILLATION LIGHT ENDS FROM THE PRODUCTION OF PHTHALIC ANHYDRIDE FROM NAPHTHALENE.',
  },
  {
    code: 'K024',
    description: 'DISTILLATION BOTTOMS FROM THE PRODUCTION OF PHTHALIC ANHYDRIDE FROM NAPHTHALENE.',
  },
  {
    code: 'K025',
    description: 'DISTILLATION BOTTOMS FROM THE PRODUCTION OF NITROBENZENE BY THE NITRATION OF BENZENE.',
  },
  {
    code: 'K026',
    description: 'STRIPPING STILL TAILS FROM THE PRODUCTION OF METHYL ETHYL PYRIDINES.',
  },
  {
    code: 'K027',
    description: 'CENTRIFUGE AND DISTILLATION RESIDUES FROM TOLUENE DIISOCYANATE PRODUCTION.',
  },
  {
    code: 'K028',
    description: 'SPENT CATALYST FROM THE HYDROCHLORINATOR REACTOR IN THE PRODUCTION OF 1,1,1-TRICHLOROETHANE.',
  },
  {
    code: 'K029',
    description: 'WASTE FROM THE PRODUCT STEAM STRIPPER IN THE PRODUCTION OF 1,1,1-TRICHLOROETHANE.',
  },
  {
    code: 'K030',
    description:
      'COLUMN BOTTOMS OR HEAVY ENDS FROM THE COMBINED PRODUCTION OF TRICHLOROETHYLENE AND PERCHLOROETHYLENE.',
  },
  {
    code: 'K031',
    description: 'BY-PRODUCT SALTS GENERATED IN THE PRODUCTION OF MSMA AND CACODYLIC ACID.',
  },
  {
    code: 'K032',
    description: 'WASTEWATER TREATMENT SLUDGE FROM THE PRODUCTION OF CHLORDANE.',
  },
  {
    code: 'K033',
    description: 'WASTEWATER AND SCRUB WATER FROM THE CHLORINATION OF CYCLOPENTADIENE IN THE PRODUCTION OF CHLORDANE.',
  },
  {
    code: 'K034',
    description: 'FILTER SOLIDS FROM THE FILTRATION OF HEXACHLOROCYCLOPENTADIENE IN THE PRODUCTION OF CHLORDANE.',
  },
  {
    code: 'K035',
    description: 'WASTEWATER TREATMENT SLUDGES GENERATED IN THE PRODUCTION OF CREOSOTE.',
  },
  {
    code: 'K036',
    description: 'STILL BOTTOMS FROM TOLUENE RECLAMATION DISTILLATION IN THE PRODUCTION OF DISULFOTON.',
  },
  {
    code: 'K037',
    description: 'WASTEWATER TREATMENT SLUDGES FROM THE PRODUCTION OF DISULFOTON.',
  },
  {
    code: 'K038',
    description: 'WASTEWATER FROM THE WASHING AND STRIPPING OF PHORATE PRODUCTION.',
  },
  {
    code: 'K039',
    description: 'FILTER CAKE FROM THE FILTRATION OF DIETHYLPHOSPHORODITHIOIC ACID IN THE PRODUCTION OF PHORATE.',
  },
  {
    code: 'K040',
    description: 'WASTEWATER TREATMENT SLUDGE FROM THE PRODUCTION OF PHORATE.',
  },
  {
    code: 'K041',
    description: 'WASTEWATER TREATMENT SLUDGE FROM THE PRODUCTION OF TOXAPHENE.',
  },
  {
    code: 'K042',
    description:
      'HEAVY ENDS OR DISTILLATION RESIDUES FROM THE DISTILLATION OF TETRACHLOROBENZENE IN THE PRODUCTION OF 2,4,5-T.',
  },
  {
    code: 'K043',
    description: '2,6-DICHLOROPHENOL WASTE FROM THE PRODUCTION OF 2,4-D.',
  },
  {
    code: 'K044',
    description: 'WASTEWATER TREATMENT SLUDGES FROM THE MANUFACTURING AND PROCESSING OF EXPLOSIVES.',
  },
  {
    code: 'K045',
    description: 'SPENT CARBON FROM THE TREATMENT OF WASTEWATER CONTAINING EXPLOSIVES.',
  },
  {
    code: 'K046',
    description:
      'WASTEWATER TREATMENT SLUDGES FROM THE MANUFACTURING, FORMULATION, AND LOADING OF LEAD-BASED INITIATING COMPOUNDS.',
  },
  {
    code: 'K047',
    description: 'PINK/RED WATER FROM TNT OPERATIONS.',
  },
  {
    code: 'K048',
    description: 'DISSOLVED AIR FLOTATION (DAF) FLOAT FROM THE PETROLEUM REFINING INDUSTRY.',
  },
  {
    code: 'K049',
    description: 'SLOP OIL EMULSION SOLIDS FROM THE PETROLEUM REFINING INDUSTRY.',
  },
  {
    code: 'K050',
    description: 'HEAT EXCHANGER BUNDLE CLEANING SLUDGE FROM THE PETROLEUM REFINING INDUSTRY.',
  },
  {
    code: 'K051',
    description: 'API SEPARATOR SLUDGE FROM THE PETROLEUM REFINING INDUSTRY.',
  },
  {
    code: 'K052',
    description: 'TANK BOTTOMS (LEADED) FROM THE PETROLEUM REFINING INDUSTRY.',
  },
  {
    code: 'K060',
    description: 'AMMONIA STILL LIME SLUDGE FROM COKING OPERATIONS.',
  },
  {
    code: 'K061',
    description: 'EMISSION CONTROL DUST/SLUDGE FROM THE PRIMARY PRODUCTION OF STEEL IN ELECTRIC FURNACES.',
  },
  {
    code: 'K062',
    description: 'SPENT PICKLE LIQUOR FROM STEEL FINISHING OPERATIONS OF PLANTS THAT PRODUCE IRON OR STEEL.',
  },
  {
    code: 'K069',
    description: 'EMISSION CONTROL DUST/SLUDGE FROM SECONDARY LEAD SMELTING.',
  },
  {
    code: 'K071',
    description:
      'BRINE PURIFICATION MUDS FROM THE MERCURY CELL PROCESS IN CHLORINE PRODUCTION, IN WHICH SEPARATELY PREPURIFIED BRINE IS NOT USED.',
  },
  {
    code: 'K073',
    description:
      'CHLORINATED HYDROCARBON WASTE FROM THE PURIFICATION STEP OF THE DIAPHRAGM CELL PROCESS USING GRAPHITE ANODES IN CHLORINE PRODUCTION.',
  },
  {
    code: 'K083',
    description: 'DISTILLATION BOTTOMS FROM ANILINE PRODUCTION.',
  },
  {
    code: 'K084',
    description:
      'WASTEWATER TREATMENT SLUDGES GENERATED DURING THE PRODUCTION OF VETERINARY PHARMACEUTICALS FROM ARSENIC OR ORGANO-ARSENIC COMPOUNDS.',
  },
  {
    code: 'K085',
    description: 'DISTILLATION OR FRACTIONATION COLUMN BOTTOMS FROM THE PRODUCTION OF CHLOROBENZENES.',
  },
  {
    code: 'K086',
    description:
      'SOLVENT WASHES AND SLUDGES, CAUSTIC WASHES AND SLUDGES, OR WATER WASHES AND SLUDGES FROM CLEANING TUBS AND EQUIPMENT USED IN THE FORMULATION OF INK FROM PIGMENTS, DRIERS, SOAPS, AND STABILIZERS CONTAINING CHROMIUM AND LEAD.',
  },
  {
    code: 'K087',
    description: 'DECANTER TANK TAR SLUDGE FROM COKING OPERATIONS.',
  },
  {
    code: 'K088',
    description: 'SPENT POTLINERS FROM PRIMARY ALUMINUM REDUCTION.',
  },
  {
    code: 'K093',
    description: 'DISTILLATION LIGHT ENDS FROM THE PRODUCTION OF PHTHALIC ANHYDRIDE FROM ORTHO-XYLENE.',
  },
  {
    code: 'K094',
    description: 'DISTILLATION BOTTOMS FROM THE PRODUCTION OF PHTHALIC ANHYDRIDE FROM ORTHO-XYLENE.',
  },
  {
    code: 'K095',
    description: 'DISTILLATION BOTTOMS FROM THE PRODUCTION OF 1,1,1-TRICHLOROETHANE.',
  },
  {
    code: 'K096',
    description: 'HEAVY ENDS FROM THE HEAVY ENDS COLUMN FROM THE PRODUCTION OF 1,1,1-TRICHLOROETHANE.',
  },
  {
    code: 'K097',
    description: 'VACUUM STRIPPER DISCHARGE FROM THE CHLORDANE CHLORINATOR IN THE PRODUCTION OF CHLORDANE.',
  },
  {
    code: 'K098',
    description: 'UNTREATED PROCESS WASTEWATER FROM THE PRODUCTION OF TOXAPHENE.',
  },
  {
    code: 'K099',
    description: 'UNTREATED WASTEWATER FROM THE PRODUCTION OF 2,4-D.',
  },
  {
    code: 'K100',
    description:
      'WASTE LEACHING SOLUTION FROM ACID LEACHING OF EMISSION CONTROL DUST/SLUDGE FROM SECONDARY LEAD SMELTING.',
  },
  {
    code: 'K101',
    description:
      'DISTILLATION TAR RESIDUES FROM THE DISTILLATION OF ANILINE-BASED COMPOUNDS IN THE PRODUCTION OF VETERINARY PHARMACEUTICALS FROM ARSENIC OR ORGANO-ARSENIC COMPOUNDS.',
  },
  {
    code: 'K102',
    description:
      'RESIDUE FROM THE USE OF ACTIVATED CARBON FOR DECOLORIZATION IN THE PRODUCTION OF VETERINARY PHARMACEUTICALS FROM ARSENIC OR ORGANO-ARSENIC COMPOUNDS.',
  },
  {
    code: 'K103',
    description: 'PROCESS RESIDUES FROM ANILINE EXTRACTION FROM THE PRODUCTION OF ANILINE.',
  },
  {
    code: 'K104',
    description: 'COMBINED WASTEWATERS GENERATED FROM NITROBENZENE/ANILINE PRODUCTION.',
  },
  {
    code: 'K105',
    description: 'SEPARATED AQUEOUS STREAM FROM THE REACTOR PRODUCT WASHING STEP IN THE PRODUCTION OF CHLOROBENZENES.',
  },
  {
    code: 'K106',
    description: 'WASTEWATER TREATMENT SLUDGE FROM THE MERCURY CELL PROCESS IN CHLORINE PRODUCTION.',
  },
  {
    code: 'K107',
    description:
      'COLUMN BOTTOMS FROM PRODUCT SEPARATION FROM THE PRODUCTION OF 1,1-DIMETHYLHYDRAZINE (UDMH) FROM CARBOXYLIC ACID HYDRAZIDES.',
  },
  {
    code: 'K108',
    description:
      'CONDENSED COLUMN OVERHEADS FROM PRODUCT SEPARATION AND CONDENSED REACTOR VENT GASES FROM THE PRODUCTION OF 1,1-DIMETHYLHYDRAZINE FROM CARBOXYLIC ACID HYDRAZIDES.',
  },
  {
    code: 'K109',
    description:
      'SPENT FILTER CARTRIDGES FROM PRODUCT PURIFICATION FROM THE PRODUCT OF 1,1-DIMETHYLHYDRAZINE FROM CARBOXYLIC ACID HYDRAZIDES.',
  },
  {
    code: 'K110',
    description:
      'CONDENSED COLUMN OVERHEADS FROM INTERMEDIATE SEPARATION FROM THE PRODUCTION OF 1,1-DIMETHYLHYDRAZINE FROM CARBOXYLIC ACID HYDRAZIDES.',
  },
  {
    code: 'K111',
    description: 'PRODUCT WASHWATERS FROM THE PRODUCTION OF DINITROTOLUENE VIA NITRATION OF TOLUENE.',
  },
  {
    code: 'K112',
    description:
      'REACTION BY-PRODUCT WATER FROM THE DRYING COLUMN IN THE PRODUCTION OF TOLUENEDIAMINE VIA HYDROGENATION OF DINITROTOLUENE.',
  },
  {
    code: 'K113',
    description:
      'CONDENSED LIQUID LIGHT ENDS FROM PURIFICATION OF TOLUENEDIAMINE IN PRODUCTION OF TOLUENEDIAMINE VIA HYDROGENATION OF DINITROTOLUENE.',
  },
  {
    code: 'K114',
    description:
      'VICINALS FROM THE PURIFICATION OF TOLUENEDIAMINE IN PRODUCTION OF TOLUENEDIAMINE VIA HYDROGENATION OF DINITROTOLUENE.',
  },
  {
    code: 'K115',
    description:
      'HEAVY ENDS FROM PURIFICATION OF TOLUENEDIAMINE IN THE PRODUCTION OF TOLUENEDIAMINE VIA HYDROGENATION OF DINITROTOLUENE.',
  },
  {
    code: 'K116',
    description:
      'ORGANIC CONDENSATE FROM THE SOLVENT RECOVERY COLUMN IN THE PRODUCTION OF TOLUENE DIISOCYANATE VIA PHOSGENATION OF TOLUENEDIAMINE.',
  },
  {
    code: 'K117',
    description:
      'WASTEWATER FROM THE REACTOR VENT GAS SCRUBBER IN THE PRODUCTION OF ETHYLENE DIBROMIDE VIA BROMINATION OF ETHENE.',
  },
  {
    code: 'K118',
    description:
      'SPENT ADSORBENT SOLIDS FROM PURIFICATION OF ETHYLENE DIBROMIDE IN THE PRODUCTION OF ETHYLENE DIBROMIDE VIA BROMINATION OF ETHENE.',
  },
  {
    code: 'K123',
    description:
      'PROCESS WASTEWATER (INCLUDING SUPERNATES, FILTRATES, AND WASHWATERS) FROM THE PRODUCTION OF ETHYLENEBISDITHIOCARBAMIC ACID AND ITS SALTS.',
  },
  {
    code: 'K124',
    description: 'REACTOR VENT SCRUBBER WATER FROM THE PRODUCTION OF ETHYLENEBISDITHIOCARBAMIC ACID AND ITS SALTS.',
  },
  {
    code: 'K125',
    description:
      'FILTRATION, EVAPORATION, AND CENTRIFUGATION SOLIDS FROM THE PRODUCTION OF ETHYLENEBISDITHIOCARBAMIC ACID AND ITS SALTS.',
  },
  {
    code: 'K126',
    description:
      'BAGHOUSE DUST AND FLOOR SWEEPINGS IN MILLING AND PACKAGING OPERATIONS FROM PRODUCTION OR FORMULATION OF ETHYLENEBISDITHIOCARBAMIC ACID AND ITS SALTS.',
  },
  {
    code: 'K131',
    description:
      'WASTEWATER FROM THE REACTOR AND SPENT SULFURIC ACID FROM THE ACID DRYER FROM THE PRODUCTION OF METHYL BROMIDE.',
  },
  {
    code: 'K132',
    description: 'SPENT ABSORBENT AND WASTEWATER SEPARATOR SOLIDS FROM THE PRODUCTION OF METHYL BROMIDE.',
  },
  {
    code: 'K136',
    description:
      'STILL BOTTOMS FROM THE PURIFICATION OF ETHYLENE DIBROMIDE IN THE PRODUCTION OF ETHYLENE DIBROMIDE VIA BROMINATION OF ETHENE.',
  },
  {
    code: 'K141',
    description:
      'PROCESS RESIDUES FROM THE RECOVERY OF COAL TAR, INCLUDING, BUT NOT LIMITED TO, TAR COLLECTING SUMP RESIDUES FROM THE PRODUCTION OF COKE FROM COAL OR THE RECOVERY OF COKE BY-PRODUCTS PRODUCED FROM COAL.  THIS LISTING DOES NOT INCLUDE K087 (DECANTER TANK SLUDGE FROM COKING OPERATIONS).',
  },
  {
    code: 'K142',
    description:
      'TANK STORAGE RESIDUES FROM THE PRODUCTION OF COKE FROM COAL OR FROM THE RECOVERY OF COKE BY-PRODUCTS FROM COAL.',
  },
  {
    code: 'K143',
    description:
      'PROCESS RESIDUES FROM THE RECOVERY OF LIGHT OIL, INCLUDING, BUT NOT LIMITED TO, THOSE GENERATED IN STILLS, DECANTERS, AND WASH OIL RECOVERY UNITS FROM THE RECOVERY OF COKE BY-PRODUCTS PRODUCED FROM COAL.',
  },
  {
    code: 'K144',
    description:
      'WASTEWATER SUMP RESIDUES FROM LIGHT OIL REFINING, INCLUDING, BUT NOT LIMITED TO, INTERCEPTING OR CONTAMINATION SUMP SLUDGES FROM THE RECOVERY OF COKE BY-PRODUCTS PRODUCED FROM COAL.',
  },
  {
    code: 'K145',
    description:
      'RESIDUES FROM NAPHTHALENE COLLECTION AND RECOVERY OPERATIONS FROM THE RECOVERY OF COKE BY-PRODUCTS PRODUCED FROM COAL.',
  },
  {
    code: 'K147',
    description: 'TAR STORAGE RESIDUES FROM COAL TAR REFINING.',
  },
  {
    code: 'K148',
    description: 'RESIDUES FROM COAL TAR DISTILLATION, INCLUDING, BUT NOT LIMITED TO, STILL BOTTOMS.',
  },
  {
    code: 'K149',
    description:
      'DISTILLATION BOTTOMS FROM THE PRODUCTION OF ALPHA (OR METHYL-) CHLORINATED TOLUNES, RING-CHLORINATED TOLUNES, BENZOYL CHLORIDES, AND COMPOUNDS WITH MIXTURES OF THESE FUNCTIONAL GROUPS.  [THIS WASTE DOES NOT INCLUDE STILL BOTTOMS FROM THE DISTILLATION OF BENZOYL CHLORIDE]',
  },
  {
    code: 'K150',
    description:
      'ORGANIC RESIDULES EXCLUDING SPENT CARBON ADSORBENT, FROM THE SPENT CHLORINE GAS AND HYDROCHLORIC ACID RECOVERY PROCESSES ASSOCIATED WITH THE PRODUCTION OF ALPHA (OR METHYL-) CHLORINATED TOLUNES, BENZOYL CHLORIDES, AND COMPOUNDS WITH MIXTURES OF THESE FUNCTIONAL GROUPS.',
  },
  {
    code: 'K151',
    description:
      'WASTEWATER TREATMENT SLUDGES, EXCLUDING NEUTRALIZATION AND BIOLOGICAL SLUDGES, GENERATED DURING THE TREATMENT OF WASTEWATERS FROM THE PRODUCTION OF ALPHA (OR METHYL-) CHLORINATED TOLUNES, BENZOYL CHLORIDES, AND COMPOUNDS WITH MIXTURES OF THESE FUNCTIONAL GROUPS.',
  },
  {
    code: 'K156',
    description:
      'ORGANIC WASTE (INCLUDING HEAVY ENDS, STILL BOTTOMS, LIGHT ENDS, SPENT SOLVENTS, FILTRATES, AND DECANTATES) FROM THE PRODUCTION OF CARBAMATES AND CARBAMOYL OXIMES.',
  },
  {
    code: 'K157',
    description:
      'WASTEWATERS (INCLUDING SCRUBBER WATERS, CONDENSER WATERS, WASHWATERS, AND SEPARATION WATERS) FROM THE PRODUCTION OF CARBAMATES AND CARBAMOYL OXIMES.',
  },
  {
    code: 'K158',
    description: 'BAG HOUSE DUSTS AND FILTER/SEPARATION SOLIDS FROM THE PRODUCTION OF CARBAMATES AND CARBAMOYL OXIMES.',
  },
  {
    code: 'K159',
    description: 'ORGANICS FROM THE TREATMENT OF THIOCARBAMATE WASTES.',
  },
  {
    code: 'K161',
    description:
      'PURIFICATION SOLIDS (INCLUDING FILTRATION, EVAPORATION, AND CENTRIFUGATION SOLIDS), BAG HOUSE DUST AND FLOOR SWEEPINGS FROM THE PRODUCTION OF DITHIOCARBAMATE ACIDS AND THEIR SALTS. (THIS LISTING DOES NOT INCLUDE K125 OR K126).',
  },
  {
    code: 'K169',
    description: 'CRUDE OIL STORAGE TANK SEDIMENT FROM PETROLEUM REFINING OPERATIONS',
  },
  {
    code: 'K170',
    description:
      'CLARIFIED SLURRY OIL TANK SEDIMENT AND/OR IN-LINE FILTER/SEPARATION SOLIDS FROM PETROLEUM REFINING OPERATIONS',
  },
  {
    code: 'K171',
    description:
      'SPENT HYDROTREATING CATALYST FROM PETROLEUM REFINING OPERATIONS,INCLUDING GUARD BEDS USED TO DESULFURIZE FEEDS TO OTHER CATALYTIC REACTORS (THIS LISTING DOES NOT INCLUDE INERT SUPPORT MEDIA)',
  },
  {
    code: 'K172',
    description:
      'SPENT HYDROREFINING CATALYST FROM PETROLEUM REFINING OPERATIONS, INCLUDING GUARD BEDS USED TO DESULFURIZE FEEDS TO OTHER CATALYTIC REACTORS (THIS LISTING DOES NOT INCLUDE INERT SUPPORT MEDIA)',
  },
  {
    code: 'K174',
    description: 'WASTEWATER TREATMENT SLUDGES FROM THE PRODUCTION OF ETHLYENE DICHLORIDE OR VINYL CHLORIDE',
  },
  {
    code: 'K175',
    description: 'WASTEWATER TREATMENT SLUDGE FROM THE PRODUCTION OF VINYL CHLORIDE MONOMER..',
  },
  {
    code: 'K176',
    description:
      'BAGHOUSE FILTERS FROM THE PRODUCTION OF ANTIMONY OXIDE, INCLUDING FILTERS FROM THE PRODUCTION OF INTERMEDIATES (E.G.,ANTIMONY METAL OR CRUDE ANTIMONY OXIDE)',
  },
  {
    code: 'K177',
    description:
      'SLAG FROM THE PRODUCTION OF ANTIMONY OXIDE THAT IS SPECULATIVELY ACCUMULATED OR DISPOSED,INCLUDING SLAG FROM THE PRODUCTION OF INTERMEDIATES (E.G.,ANTIMONY METAL OR CRUDE ANTIMONY OXIDE)',
  },
  {
    code: 'K178',
    description:
      'RESIDUES FROM MANUFACTURING AND MANUFACTURING-SITE STORAGE OF FERRIC CHLORIDE FROM ACIDS FORMED DURING THE PRODUCTION OF TITANIUM DIOXIDE USING THE CHLORIDE-ILMENITE PROCESS.',
  },
  {
    code: 'K181',
    description: 'Nonwastewaters from the production of dyes and/or pigments.',
  },
  {
    code: 'LABP',
    description: 'LAB PACK',
  },
  {
    code: 'P001',
    description:
      '2H-1-BENZOPYRAN-2-ONE, 4-HYDROXY-3-(3-OXO-1-PHENYLBUTYL)-, & SALTS, WHEN PRESENT AT CONCENTRATIONS GREATER THAN 0.3% (OR) WARFARIN, & SALTS, WHEN PRESENT AT CONCENTRATIONS GREATER THAN 0.3%',
  },
  {
    code: 'P002',
    description: '1-ACETYL-2-THIOUREA (OR) ACETAMIDE, N-(AMINOTHIOXOMETHYL)-',
  },
  {
    code: 'P003',
    description: '2-PROPENAL (OR) ACROLEIN',
  },
  {
    code: 'P004',
    description:
      '1,4,5,8-DIMETHANONAPHTHALENE, 1,2,3,4,10,10-HEXA-CHLORO-1,4,4A,5,8,8A,-HEXAHYDRO-, (1ALPHA, 4ALPHA, 4ABETA, 5ALPHA, 8ALPHA, 8ABETA)- (OR) ALDRIN',
  },
  {
    code: 'P005',
    description: '2-PROPEN-1-OL (OR) ALLYL ALCOHOL',
  },
  {
    code: 'P006',
    description: 'ALUMINUM PHOSPHIDE (R,T)',
  },
  {
    code: 'P007',
    description: '3(2H)-ISOXAZOLONE, 5-(AMINOMETHYL)- (OR) 5-(AMINOMETHYL)-3-ISOXAZOLOL',
  },
  {
    code: 'P008',
    description: '4-AMINOPYRIDINE (OR) 4-PYRIDINAMINE',
  },
  {
    code: 'P009',
    description: 'AMMONIUM PICRATE (R) (OR) PHENOL, 2,4,6-TRINITRO-, AMMONIUM SALT (R)',
  },
  {
    code: 'P010',
    description: 'ARSENIC ACID H3ASO4',
  },
  {
    code: 'P011',
    description: 'ARSENIC OXIDE AS2O5 (OR) ARSENIC PENTOXIDE',
  },
  {
    code: 'P012',
    description: 'ARSENIC OXIDE AS2O3 (OR) ARSENIC TRIOXIDE',
  },
  {
    code: 'P013',
    description: 'BARIUM CYANIDE',
  },
  {
    code: 'P014',
    description: 'BENZENETHIOL (OR) THIOPHENOL',
  },
  {
    code: 'P015',
    description: 'BERYLLIUM',
  },
  {
    code: 'P016',
    description: 'DICHLOROMETHYL ETHER (OR) METHANE, OXYBIS[CHLORO-',
  },
  {
    code: 'P017',
    description: '2-PROPANONE, 1-BROMO- (OR) BROMOACETONE',
  },
  {
    code: 'P018',
    description: 'BRUCINE (OR) STRYCHNIDIN-10-ONE, 2,3-DIMETHOXY-',
  },
  {
    code: 'P020',
    description: 'DINOSEB (OR) PHENOL, 2-(1-METHYLPROPYL)-4,6-DINITRO-',
  },
  {
    code: 'P021',
    description: 'CALCIUM CYANIDE (OR) CALCIUM CYANIDE CA(CN)2',
  },
  {
    code: 'P022',
    description: 'CARBON DISULFIDE',
  },
  {
    code: 'P023',
    description: 'ACETALDEHYDE, CHLORO- (OR) CHLOROACETALDEHYDE',
  },
  {
    code: 'P024',
    description: 'BENZENAMINE, 4-CHLORO- (OR) P-CHLORANILINE',
  },
  {
    code: 'P026',
    description: '1-(O-CHLOROPHENYL)THIOUREA (OR) THIOUREA, (2-CHLOROPHENYL)-',
  },
  {
    code: 'P027',
    description: '3-CHLOROPROPIONITRILE (OR) PROPANENITRILE, 3-CHLORO-',
  },
  {
    code: 'P028',
    description: 'BENZENE, (CHLOROMETHYL)- (OR) BENZYL CHLORIDE',
  },
  {
    code: 'P029',
    description: 'COPPER CYANIDE (OR) COPPER CYANIDE CU(CN)',
  },
  {
    code: 'P030',
    description: 'CYANIDES (SOLUBLE CYANIDE SALTS), NOT OTHERWISE SPECIFIED',
  },
  {
    code: 'P031',
    description: 'CYANOGEN (OR) ETHANEDINITRILE',
  },
  {
    code: 'P033',
    description: 'CYANOGEN CHLORIDE (OR) CYANOGEN CHLORIDE (CN)CL',
  },
  {
    code: 'P034',
    description: '2-CYCLOHEXYL-4,6-DINITROPHENOL (OR) PHENOL, 2-CYCLOHEXYL-4,6-DINITRO-',
  },
  {
    code: 'P036',
    description: 'ARSONOUS DICHLORIDE, PHENYL- (OR) DICHLOROPHENYLARSINE',
  },
  {
    code: 'P037',
    description:
      '2,7:3,6-DIMETHANONAPHTH[2,3-B]OXIRENE, 3,4,5,6,9,9-HEXACHLORO-1A,2,2A,3,6,6A,7,7A-OCTAHYDRO-, (1AALPHA, 2BETA, 2AALPHA, 3BETA, 6BETA, 6AALPHA, 7BETA, 7AALPHA)- (OR) DIELDRIN',
  },
  {
    code: 'P038',
    description: 'ARSINE, DIETHYL- (OR) DIETHYLARSINE',
  },
  {
    code: 'P039',
    description: 'DISULFOTON (OR) PHOSPHORODITHIOIC ACID, O,O-DIETHYL S-[2-(ETHYLTHIO)ETHYL] ESTER',
  },
  {
    code: 'P040',
    description: 'O,O-DIETHYL O-PYRAZINYL PHOSPHOROTHIOATE (OR) PHOSPHOROTHIOIC ACID, O,O-DIETHYL O-PYRAZINYL ESTER',
  },
  {
    code: 'P041',
    description: 'DIETHYL-P-NITROPHENYL PHOSPHATE (OR) PHOSPHORIC ACID, DIETHYL 4-NITROPHENYL ESTER',
  },
  {
    code: 'P042',
    description: '1,2-BENZENEDIOL, 4-[1-HYDROXY-2-(METHYLAMINO)ETHYL]-, (R)- (OR) EPINEPHRINE',
  },
  {
    code: 'P043',
    description: 'DIISOPROPYLFLUOROPHOSPHATE (DFP) (OR) PHOSPHOROFLUORIDIC ACID, BIS(1-METHYLETHYL) ESTER',
  },
  {
    code: 'P044',
    description: 'DIMETHOATE (OR) PHOSPHORODITHIOIC ACID, O,O-DIMETHYL S-[2-(METHYLAMINO)-2-OXOETHYL] ESTER',
  },
  {
    code: 'P045',
    description: '2-BUTANONE, 3,3-DIMETHYL-1-(METHYLTHIO)-, O-[METHYLAMINO)CARBONYL] OXIME (OR) THIOFANOX',
  },
  {
    code: 'P046',
    description: 'ALPHA,ALPHA-DIMETHYLPHENETHYLAMINE (OR) BENZENEETHANAMINE, ALPHA, ALPHA-DIMETHYL-',
  },
  {
    code: 'P047',
    description: '4,6-DINITRO-O-CRESOL, & SALTS (OR) PHENOL, 2-METHYL-4,6-DINITRO-, & SALTS',
  },
  {
    code: 'P048',
    description: '2,4-DINITROPHENOL (OR) PHENOL, 2,4-DINITRO-',
  },
  {
    code: 'P049',
    description: 'DITHIOBIURET (OR) THIOIMIDODICARBONIC DIAMIDE [(H2N)C(S)]2NH',
  },
  {
    code: 'P050',
    description:
      '6,9-METHANO-2,4,3-BENZODIOXATHIEPIN,6,7,8,9,10,10-HEXACHLORO-1,5,5A,6,9,9A-HEXAHYDRO-,3-OXIDE (OR) ENDOSULFAN',
  },
  {
    code: 'P051',
    description:
      '2,7:3,6-DIMETHANONAPHTH[2,3-B]OXIRENE, 3,4,5,6,9,9-HEXACHLORO-1A,2,2A,3,6,6A,7,7A-OCTAHYDRO-, (1AALPHA, 2BETA, 2ABETA, 3ALPHA, 6ALPHA, 6ABETA, 7BETA, 7AALPHA)- & METABOLITES (OR) ENDRIN (OR) ENDRIN, & METABOLITES',
  },
  {
    code: 'P054',
    description: 'AZIRIDINE (OR) ETHYLENEIMINE',
  },
  {
    code: 'P056',
    description: 'FLUORINE',
  },
  {
    code: 'P057',
    description: 'ACETAMIDE, 2-FLUORO- (OR) FLUOROACETAMIDE',
  },
  {
    code: 'P058',
    description: 'ACETIC ACID, FLUORO-, SODIUM SALT (OR) FLUOROACETIC ACID, SODIUM SALT',
  },
  {
    code: 'P059',
    description: '4,7-METHANO-1H-INDENE, 1,4,5,6,7,8,8-HEPTACHLORO-3A,4,7,7A-TETRAHYDRO- (OR) HEPTACHLOR',
  },
  {
    code: 'P060',
    description:
      '1,4,5,8-DIMETHANONAPHTHALENE, 1,2,3,4,10,10-HEXA-CHLORO-1,4,4A,5,8,8A,-HEXAHYDRO-, (1ALPHA, 4ALPHA, 4ABETA, 5BETA, 8BETA, 8ABETA)- (OR) ISODRIN',
  },
  {
    code: 'P062',
    description: 'HEXAETHYL TETRAPHOSPHATE (OR) TETRAPHOSPHORIC ACID, HEXAETHYL ESTER',
  },
  {
    code: 'P063',
    description: 'HYDROCYANIC ACID (OR) HYDROGEN CYANIDE',
  },
  {
    code: 'P064',
    description: 'METHANE, ISOCYANATO- (OR) METHYL ISOCYANATE',
  },
  {
    code: 'P065',
    description: 'FULMINIC ACID, MERCURY(2+) SALT (R,T) (OR) MERCURY FULMINATE (R,T)',
  },
  {
    code: 'P066',
    description: 'ETHANIMIDOTHIOIC ACID, N-[[(METHYLAMINO)CARBONYL]OXY]-, METHYL ESTER (OR) METHOMYL',
  },
  {
    code: 'P067',
    description: '1,2-PROPYLENIMINE (OR) AZIRIDINE, 2-METHYL-',
  },
  {
    code: 'P068',
    description: 'HYDRAZINE, METHYL- (OR) METHYL HYDRAZINE',
  },
  {
    code: 'P069',
    description: '2-METHYLLACTONITRILE (OR) PROPANENITRILE, 2-HYDROXY-2-METHYL-',
  },
  {
    code: 'P070',
    description: 'ALDICARB (OR) PROPANAL, 2-METHYL-2-(METHYLTHIO)-, O-[(METHYLAMINO)CARBONYL]OXIME',
  },
  {
    code: 'P071',
    description: 'METHYL PARATHION (OR) PHOSPHOROTHIOIC ACID, O,O,-DIMETHYL O-(4-NITROPHENYL) ESTER',
  },
  {
    code: 'P072',
    description: 'ALPHA-NAPHTHYLTHIOUREA (OR) THIOUREA, 1-NAPHTHALENYL-',
  },
  {
    code: 'P073',
    description: 'NICKEL CARBONYL (OR) NICKEL CARBONYL NI(CO)4, (T-4)-',
  },
  {
    code: 'P074',
    description: 'NICKEL CYANIDE (OR) NICKEL CYANIDE NI(CN)2',
  },
  {
    code: 'P075',
    description: 'NICOTINE, & SALTS (OR) PYRIDINE, 3-(1-METHYL-2-PYRROLIDINYL)-,(S)-, & SALTS',
  },
  {
    code: 'P076',
    description: 'NITRIC OXIDE (OR) NITROGEN OXIDE NO',
  },
  {
    code: 'P077',
    description: 'BENZENAMINE, 4-NITRO- (OR) P-NITROANILINE',
  },
  {
    code: 'P078',
    description: 'NITROGEN DIOXIDE (OR) NITROGEN OXIDE NO2',
  },
  {
    code: 'P081',
    description: '1,2,3-PROPANETRIOL, TRINITRATE (R) (OR) NITROGLYCERINE (R)',
  },
  {
    code: 'P082',
    description: 'METHANIMINE, N-METHYL-N-NITROSO- (OR) N-NITROSODIMETHYLAMINE',
  },
  {
    code: 'P084',
    description: 'N-NITROSOMETHYLVINYLAMINE (OR) VINYLAMINE, N-METHYL-N-NITROSO-',
  },
  {
    code: 'P085',
    description: 'DIPHOSPHORAMIDE, OCTAMETHYL- (OR) OCTAMETHYLPYROPHOSPHORAMIDE',
  },
  {
    code: 'P087',
    description: 'OSMIUM OXIDE OSO4, (T-4)- (OR) OSMIUM TETROXIDE',
  },
  {
    code: 'P088',
    description: '7-OXABICYCLO[2.2.1]HEPTANE-2,3-DICARBOXYLIC ACID (OR) ENDOTHALL',
  },
  {
    code: 'P089',
    description: 'PARATHION (OR) PHOSPHOROTHIOIC ACID, O,O-DIETHYL-O-(4-NITROPHENYL) ESTER',
  },
  {
    code: 'P092',
    description: 'MERCURY, (ACETATO-O)PHENYL- (OR) PHENYLMERCURY ACETATE',
  },
  {
    code: 'P093',
    description: 'PHENYLTHIOUREA (OR) THIOUREA, PHENYL-',
  },
  {
    code: 'P094',
    description: 'PHORATE (OR) PHOSPHORODITHIOIC ACID, O,O-DIETHYL S-[(ETHYLTHIO)METHYL] ESTER',
  },
  {
    code: 'P095',
    description: 'CARBONIC DICHLORIDE (OR) PHOSGENE',
  },
  {
    code: 'P096',
    description: 'HYDROGEN PHOSPHIDE (OR) PHOSPHINE',
  },
  {
    code: 'P097',
    description: 'FAMPHUR (OR) PHOSPHOROTHIOIC ACID O-[4-[(DIMETHYLAMINO)SULFONYL]PHENYL] O,O-DIMETHYL ESTER',
  },
  {
    code: 'P098',
    description: 'POTASSIUM CYANIDE (OR) POTASSIUM CYANIDE K(CN)',
  },
  {
    code: 'P099',
    description: 'ARGENTATE (1-), BIS(CYANO-C)-, POTASSIUM (OR) POTASSIUM SILVER CYANIDE',
  },
  {
    code: 'P101',
    description: 'ETHYL CYANIDE (OR) PROPANENITRILE',
  },
  {
    code: 'P102',
    description: '2-PROPYN-1-OL (OR) PROPARGYL ALCOHOL',
  },
  {
    code: 'P103',
    description: 'SELENOUREA',
  },
  {
    code: 'P104',
    description: 'SILVER CYANIDE (OR) SILVER CYANIDE AG(CN)',
  },
  {
    code: 'P105',
    description: 'SODIUM AZIDE',
  },
  {
    code: 'P106',
    description: 'SODIUM CYANIDE (OR) SODIUM CYANIDE NA(CN)',
  },
  {
    code: 'P108',
    description: 'STRYCHNIDIN-10-ONE, & SALTS (OR) STRYCHNINE, & SALTS',
  },
  {
    code: 'P109',
    description: 'TETRAETHYLDITHIOPYROPHOSPHATE (OR) THIODIPHOSPHORIC ACID, TETRAETHYL ESTER',
  },
  {
    code: 'P110',
    description: 'PLUMBANE, TETRAETHYL- (OR) TETRAETHYL LEAD',
  },
  {
    code: 'P111',
    description: 'DIPHOSPHORIC ACID, TETRAETHYL ESTER (OR) TETRAETHYL PYROPHOSPHATE',
  },
  {
    code: 'P112',
    description: 'METHANE, TETRANITRO- (R) (OR) TETRANITROMETHANE (R)',
  },
  {
    code: 'P113',
    description: 'THALLIC OXIDE (OR) THALLIUM OXIDE TL2O3',
  },
  {
    code: 'P114',
    description: 'SELENIOUS ACID, DITHALLIUM (1+) SALT (OR) THALLIUM(I) SELENITE',
  },
  {
    code: 'P115',
    description: 'SULFURIC ACID, DITHALLIUM (1+) SALT (OR) THALLIUM(I) SULFATE',
  },
  {
    code: 'P116',
    description: 'HYDRAZINECARBOTHIOAMIDE (OR) THIOSEMICARBAZIDE',
  },
  {
    code: 'P118',
    description: 'METHANETHIOL, TRICHLORO- (OR) TRICHLOROMETHANETHIOL',
  },
  {
    code: 'P119',
    description: 'AMMONIUM VANADATE (OR) VANADIC ACID, AMMONIUM SALT',
  },
  {
    code: 'P120',
    description: 'VANADIUM OXIDE V2O5 (OR) VANADIUM PENTOXIDE',
  },
  {
    code: 'P121',
    description: 'ZINC CYANIDE (OR) ZINC CYANIDE ZN(CN)2',
  },
  {
    code: 'P122',
    description: 'ZINC PHOSPHIDE ZN3P2, WHEN PRESENT AT CONCENTRATIONS GREATER THAN 10% (R,T)',
  },
  {
    code: 'P123',
    description: 'TOXAPHENE',
  },
  {
    code: 'P124',
    description: 'ACTINOMYCIN D',
  },
  {
    code: 'P127',
    description: '7-BENZOFURANOL, 2,3-DIHYDRO-2,2-DIMETHYL-, METHYLCARBAMATE (OR) CARBOFURAN',
  },
  {
    code: 'P128',
    description: 'PHENOL, 4-(DIMETHYLAMINO)-3,5-DIMETHYL-, METHYLCARBAMATE (ESTER)',
  },
  {
    code: 'P185',
    description: '1,3-DITHIOLANE-2-CARBOXALDEHYDE, 2,4-DIMETHYL-, O- [(METHYLAMINO)-CARBONYL]OXIME (OR) TIRPATE',
  },
  {
    code: 'P188',
    description:
      'BENZOIC ACID, 2-HYDROXY-, COMPD. WITH (3AS-CIS)-1,2,3,3A,8,8A-HEXAHYDRO-1,3A,8-TRIMETHYLPYRROLO[2,3-B]INDOL-5-YL METHYLCARBAMATE ESTER (1:1) (OR) PHYSOSTIGMINE SALICYLATE',
  },
  {
    code: 'P189',
    description:
      'CARBAMIC ACID, [(DIBUTYLAMINO)-THIO]METHYL-, 2,3-DIHYDRO-2,2-DIMETHYL -7-BENZOFURANYL ESTER (OR) CARBOSULFAN',
  },
  {
    code: 'P190',
    description: 'CARBAMIC ACID, METHYL-, 3-METHYLPHENYL ESTER (OR) METOLCARB',
  },
  {
    code: 'P191',
    description:
      'CARBAMIC ACID, DIMETHYL-, 1-[(DIMETHYL-AMINO)CARBONYL]- 5-METHYL-1H- PYRAZOL-3-YL ESTER (OR) DIMETILAN',
  },
  {
    code: 'P192',
    description: 'ISOLAN (OR) CARBAMIC ACID, DIMETHYL-, 3-METHY-L-(1-METHYLETHYL)-1H- PYRAZOL-5-YL ESTER',
  },
  {
    code: 'P194',
    description:
      'ETHANIMIDOTHIOC ACID, 2-(DIMETHYLAMINO)-N-[[(METHYLAMINO) CARBONYL]OXY]-2-OXO-, METHYL ESTER (OR) OXAMYL',
  },
  {
    code: 'P196',
    description: "MANGANESE DIMETHYLDITHIOCARBAMATE (OR) MANGANESE, BIS(DIMETHYLCARBAMODITHIOATO-S,S')-,",
  },
  {
    code: 'P197',
    description: "FORMPARANATE (OR) METHANIMIDAMIDE, N,N-DIMETHYL-N'-[2-METHYL-4-[[(METHYLAMINO)CARBONYL]OXY]PHENYL]",
  },
  {
    code: 'P198',
    description:
      "METHANIMIDAMIDE, N,N-DIMETHYL-N'-[3-[[(METHYLAMINO)-CARBONYL]OXY]PHENYL]-, MONOHYDROCHLORIDE (OR) FORMETANATE HYDROCHLORIDE",
  },
  {
    code: 'P199',
    description: 'METHIOCARB (OR) MEXACARBATE (OR) PHENOL, (3,5-DIMETHYL-4-(METHYLTHIO)-, METHYLCARBAMATE',
  },
  {
    code: 'P201',
    description: 'PHENOL, 3-METHYL-5-(1-METHYLETHYL)-, METHYL CARBAMATE (OR) PROMECARB',
  },
  {
    code: 'P202',
    description:
      'M-CUMENYL METHYLCARBAMATE (OR) 3-ISOPROPYLPHENYL N-METHYLCARBAMATE (OR) PHENOL, 3-(1-METHYLETHYL)-, METHYL CARBAMATE',
  },
  {
    code: 'P203',
    description: 'ALDICARB SULFONE (OR) PROPANAL, 2-METHYL-2-(METHYL-SULFONYL)-, O-[(METHYLAMINO)CARBONYL] OXIME',
  },
  {
    code: 'P204',
    description:
      'PHYSOSTIGMINE (OR) PYRROLO[2,3-B]INDOL-5-OL, 1,2,3,3A,8,8A-HEXAHYDRO-1,3A,8-TRIMETHYL-METHYLCARBAMATE (ESTER), (3AS-CIS)-',
  },
  {
    code: 'P205',
    description: "ZINC, BIS(DIMETHYLCARBAMODITHIOATO-S,S')-, (OR) ZIRAM",
  },
  {
    code: 'PHARMS',
    description: 'Hazardous Waste Pharmaceuticals',
  },
  {
    code: 'PHRM',
    description: 'HAZARDOUS WASTE PHARMACEUTICALS (OWNER = US)',
  },
  {
    code: 'U001',
    description: 'ACETALDEHYDE (I) (OR) ETHANAL (I)',
  },
  {
    code: 'U002',
    description: '2-PROPANONE (I) (OR) ACETONE (I)',
  },
  {
    code: 'U003',
    description: 'ACETONITRILE (I,T)',
  },
  {
    code: 'U004',
    description: 'ACETOPHENONE (OR) ETHANONE, 1-PHENYL-',
  },
  {
    code: 'U005',
    description: '2-ACETYLAMINOFLUORENE (OR) ACETAMIDE, N-9H-FLUOREN-2-YL',
  },
  {
    code: 'U006',
    description: 'ACETYL CHLORIDE (C,R,T)',
  },
  {
    code: 'U007',
    description: '2-PROPENAMIDE (OR) ACRYLAMIDE',
  },
  {
    code: 'U008',
    description: '2-PROPENOIC ACID (I) (OR) ACRYLIC ACID (I)',
  },
  {
    code: 'U009',
    description: '2-PROPENENITRILE (OR) ACRYLONITRILE',
  },
  {
    code: 'U010',
    description:
      "AZIRINO [2',3':3,4]PYRROLO[1,2-A]INDOLE-4,7-DIONE, 6-AMINO-8-[[(AMINOCARBONYL)OXY]METHYL]-1,1A,2,8,8A,8B-HEXAHYDRO-8A-METHOXY-5-METHYL-, [1AS-(1AALPHA, 8BETA, 8AALPHA, 8BALPHA)]- (OR) MITOMYCIN C",
  },
  {
    code: 'U011',
    description: '1H-1,2,4-TRIAZOL-3-AMINE (OR) AMITROLE',
  },
  {
    code: 'U012',
    description: 'ANILINE (I,T) (OR) BENZENAMINE (I,T)',
  },
  {
    code: 'U014',
    description: "AURAMINE (OR) BENZENAMINE, 4,4'-CARBONIMIDOYLBIS[N,N-DIMETHYL-",
  },
  {
    code: 'U015',
    description: 'AZASERINE (OR) L-SERINE, DIAZOACETATE (ESTER)',
  },
  {
    code: 'U016',
    description: 'BENZ[C]ACRIDINE',
  },
  {
    code: 'U017',
    description: 'BENZAL CHLORIDE (OR) BENZENE, (DICHLOROMETHYL)-',
  },
  {
    code: 'U018',
    description: 'BENZ[A]ANTHRACENE',
  },
  {
    code: 'U019',
    description: 'BENZENE (I,T)',
  },
  {
    code: 'U020',
    description: 'BENZENESULFONIC ACID CHLORIDE (C,R) (OR) BENZENESULFONYL CHLORIDE (C,R)',
  },
  {
    code: 'U021',
    description: "[1,1'-BIPHENYL]-4,4'-DIAMINE (OR) BENZIDINE",
  },
  {
    code: 'U022',
    description: 'BENZO[A]PYRENE',
  },
  {
    code: 'U023',
    description: 'BENZENE, (TRICHLOROMETHYL)- (OR) BENZOTRICHLORIDE (C,R,T)',
  },
  {
    code: 'U024',
    description: "DICHLOROMETHOXY ETHANE (OR) ETHANE, 1,1'-[METHYLENEBIS(OXY)]BIS[2-CHLORO-",
  },
  {
    code: 'U025',
    description: "DICHLOROETHYL ETHER (OR) ETHANE, 1,1'-OXYBIS[2-CHLORO-",
  },
  {
    code: 'U026',
    description: "CHLORNAPHAZIN (OR) NAPHTHALENAMINE, N,N'-BIS(2-CHLOROETHYL)-",
  },
  {
    code: 'U027',
    description: "DICHLOROISOPROPYL ETHER (OR) PROPANE, 2,2'-OXYBIS[2-CHLORO-",
  },
  {
    code: 'U028',
    description: '1,2-BENZENEDICARBOXYLIC ACID, BIS(2-ETHYLHEXYL) ESTER (OR) DIETHYLHEXYL PHTHALATE',
  },
  {
    code: 'U029',
    description: 'METHANE, BROMO- (OR) METHYL BROMIDE',
  },
  {
    code: 'U030',
    description: '4-BROMOPHENYL PHENYL ETHER (OR) BENZENE, 1-BROMO-4-PHENOXY-',
  },
  {
    code: 'U031',
    description: '1-BUTANOL (I) (OR) N-BUTYL ALCOHOL (I)',
  },
  {
    code: 'U032',
    description: 'CALCIUM CHROMATE (OR) CHROMIC ACID H2CRO4, CALCIUM SALT',
  },
  {
    code: 'U033',
    description: 'CARBON OXYFLUORIDE (R,T) (OR) CARBONIC DIFLUORIDE',
  },
  {
    code: 'U034',
    description: 'ACETALDEHYDE, TRICHLORO- (OR) CHLORAL',
  },
  {
    code: 'U035',
    description: 'BENZENEBUTANOIC ACID, 4-[BIS(2-CHLOROETHYL)AMINO]- (OR) CHLORAMBUCIL',
  },
  {
    code: 'U036',
    description:
      '4,7-METHANO-1H-INDENE, 1,2,4,5,6,7,8,8-OCTACHLORO-2,3,3A,4,7,7A-HEXAHYDRO- (OR) CHLORDANE, ALPHA & GAMMA ISOMERS',
  },
  {
    code: 'U037',
    description: 'BENZENE, CHLORO- (OR) CHLOROBENZENE',
  },
  {
    code: 'U038',
    description: 'BENZENEACETIC ACID, 4-CHLORO-ALPHA-(4-CHLOROPHENYL)-ALPHA-HYDROXY-, ETHYL ESTER (OR) CHLOROBENZILATE',
  },
  {
    code: 'U039',
    description: 'P-CHLORO-M-CRESOL (OR) PHENOL, 4-CHLORO-3-METHYL-',
  },
  {
    code: 'U041',
    description: 'EPICHLOROHYDRIN (OR) OXIRANE, (CHLOROMETHYL)-',
  },
  {
    code: 'U042',
    description: '2-CHLOROETHYL VINYL ETHER (OR) ETHENE, (2-CHLOROETHOXY)-',
  },
  {
    code: 'U043',
    description: 'ETHENE, CHLORO- (OR) VINYL CHLORIDE',
  },
  {
    code: 'U044',
    description: 'CHLOROFORM (OR) METHANE, TRICHLORO-',
  },
  {
    code: 'U045',
    description: 'METHANE, CHLORO- (I,T) (OR) METHYL CHLORIDE (I,T)',
  },
  {
    code: 'U046',
    description: 'CHLOROMETHYL METHYL ETHER (OR) METHANE, CHLOROMETHOXY-',
  },
  {
    code: 'U047',
    description: 'BETA-CHLORONAPHTHALENE (OR) NAPHTHALENE, 2-CHLORO-',
  },
  {
    code: 'U048',
    description: 'O-CHLOROPHENOL (OR) PHENOL, 2-CHLORO-',
  },
  {
    code: 'U049',
    description: '4-CHLORO-O-TOLUIDINE, HYDROCHLORIDE (OR) BENZENAMINE, 4-CHLORO-2-METHYL-, HYDROCHLORIDE',
  },
  {
    code: 'U050',
    description: 'CHRYSENE',
  },
  {
    code: 'U051',
    description: 'CREOSOTE',
  },
  {
    code: 'U052',
    description: 'CRESOL (CRESYLIC ACID) (OR) PHENOL, METHYL-',
  },
  {
    code: 'U053',
    description: '2-BUTENAL (OR) CROTONALDEHYDE',
  },
  {
    code: 'U055',
    description: 'BENZENE, (1-METHYLETHYL)- (I) (OR) CUMENE (I)',
  },
  {
    code: 'U056',
    description: 'BENZENE, HEXAHYDRO- (I) (OR) CYCLOHEXANE (I)',
  },
  {
    code: 'U057',
    description: 'CYCLOHEXANONE (I)',
  },
  {
    code: 'U058',
    description: '2H-1,3,2-OXAZAPHOSPHORIN-2-AMINE, N,N-BIS(2-CHLOROETHYL)TETRAHYDRO-, 2-OXIDE (OR) CYCLOPHOSPHAMIDE',
  },
  {
    code: 'U059',
    description:
      '5,12-NAPHTHACENEDIONE, 8-ACETYL-10-[(3-AMINO-2,3,6-TRIDEOXY)-ALPHA-L-LYXO-HEXOPYRANOSYL)OXY]-7,8,9,10-TETRAHYDRO-6,8,11-TRIHYDROXY-1-METHOXY-, (8S-CIS)- (OR) DAUNOMYCIN',
  },
  {
    code: 'U060',
    description: "BENZENE, 1,1'-(2,2-DICHLOROETHYLIDENE)BIS[4-CHLORO- (OR) DDD",
  },
  {
    code: 'U061',
    description: "BENZENE, 1,1'-(2,2,2-TRICHLOROETHYLIDENE)BIS[4-CHLORO- (OR) DDT",
  },
  {
    code: 'U062',
    description: 'CARBAMOTHIOIC ACID, BIS(1-METHYLETHYL)-, S-(2,3-DICHLORO-2-PROPENYL) ESTER (OR) DIALLATE',
  },
  {
    code: 'U063',
    description: 'DIBENZ[A,H]ANTHRACENE',
  },
  {
    code: 'U064',
    description: 'BENZO[RST]PENTAPHENE (OR) DIBENZO[A,I]PYRENE',
  },
  {
    code: 'U066',
    description: '1,2-DIBROMO-3-CHLOROPROPANE (OR) PROPANE, 1,2-DIBROMO-3-CHLORO-',
  },
  {
    code: 'U067',
    description: 'ETHANE, 1,2-DIBROMO- (OR) ETHYLENE DIBROMIDE',
  },
  {
    code: 'U068',
    description: 'METHANE, DIBROMO- (OR) METHYLENE BROMIDE',
  },
  {
    code: 'U069',
    description: '1,2-BENZENEDICARBOXYLIC ACID, DIBUTYL ESTER (OR) DIBUTYL PHTHALATE',
  },
  {
    code: 'U070',
    description: 'BENZENE, 1,2-DICHLORO- (OR) O-DICHLOROBENZENE',
  },
  {
    code: 'U071',
    description: 'BENZENE, 1,3-DICHLORO- (OR) M-DICHLOROBENZENE',
  },
  {
    code: 'U072',
    description: 'BENZENE, 1,4-DICHLORO- (OR) P-DICHLOROBENZENE',
  },
  {
    code: 'U073',
    description: "[1,1'-BIPHENYL]-4,4'-DIAMINE, 3,3'-DICHLORO- (OR) 3,3'-DICHLOROBENZIDINE",
  },
  {
    code: 'U074',
    description: '1,4-DICHLORO-2-BUTENE (I,T) (OR) 2-BUTENE, 1,4-DICHLORO- (I,T)',
  },
  {
    code: 'U075',
    description: 'DICHLORODIFLUOROMETHANE (OR) METHANE, DICHLORODIFLUORO-',
  },
  {
    code: 'U076',
    description: 'ETHANE, 1,1-DICHLORO- (OR) ETHYLIDENE DICHLORIDE',
  },
  {
    code: 'U077',
    description: 'ETHANE, 1,2-DICHLORO- (OR) ETHYLENE DICHLORIDE',
  },
  {
    code: 'U078',
    description: '1,1-DICHLOROETHYLENE (OR) ETHENE, 1,1-DICHLORO-',
  },
  {
    code: 'U079',
    description: '1,2-DICHLOROETHYLENE (OR) ETHENE, 1,2-DICHLORO-,(E)-',
  },
  {
    code: 'U080',
    description: 'METHANE, DICHLORO- (OR) METHYLENE CHLORIDE',
  },
  {
    code: 'U081',
    description: '2,4-DICHLOROPHENOL (OR) PHENOL, 2,4-DICHLORO-',
  },
  {
    code: 'U082',
    description: '2,6-DICHLOROPHENOL (OR) PHENOL, 2,6-DICHLORO-',
  },
  {
    code: 'U083',
    description: 'PROPANE, 1,2-DICHLORO- (OR) PROPYLENE DICHLORIDE',
  },
  {
    code: 'U084',
    description: '1,3-DICHLOROPROPENE (OR) 1-PROPENE, 1,3-DICHLORO-',
  },
  {
    code: 'U085',
    description: "1,2:3,4-DIEPOXYBUTANE (I,T) (OR) 2,2'-BIOXIRANE",
  },
  {
    code: 'U086',
    description: "HYDRAZINE, 1,2-DIETHYL- (OR) N,N'-DIETHYLHYDRAZINE",
  },
  {
    code: 'U087',
    description: 'O,O-DIETHYL S-METHYL DITHIOPHOSPHATE (OR) PHOSPHORODITHIOIC ACID, O,O-DIETHYL S-METHYL ESTER',
  },
  {
    code: 'U088',
    description: '1,2-BENZENEDICARBOXYLIC ACID, DIETHYL ESTER (OR) DIETHYL PHTHALATE',
  },
  {
    code: 'U089',
    description: "DIETHYLSTILBESTEROL (OR) PHENOL, 4,4'-(1,2-DIETHYL-1,2-ETHENEDIYL)BIS, (E)-",
  },
  {
    code: 'U090',
    description: '1,3-BENZODIOXOLE, 5-PROPYL- (OR) DIHYDROSAFROLE',
  },
  {
    code: 'U091',
    description: "[1,1'-BIPHENYL]-4,4'-DIAMINE, 3,3'-DIMETHOXY- (OR) 3,3'-DIMETHOXYBENZIDINE",
  },
  {
    code: 'U092',
    description: 'DIMETHYLAMINE (I) (OR) METHANAMINE, N-METHYL- (I)',
  },
  {
    code: 'U093',
    description: 'BENZENAMINE, N,N-DIMETHYL-4-(PHENYLAZO)- (OR) P-DIMETHYLAMINOAZOBENZENE',
  },
  {
    code: 'U094',
    description: '7,12-DIMETHYLBENZ[A]ANTHRACENE (OR) BENZ[A]ANTHRACENE, 7,12-DIMETHYL-',
  },
  {
    code: 'U095',
    description: "[1,1'-BIPHENYL]-4,4'-DIAMINE, 3,3'-DIMETHYL- (OR) 3,3'-DIMETHYLBENZIDINE",
  },
  {
    code: 'U096',
    description: 'ALPHA,ALPHA-DIMETHYLBENZYLHYDROPEROXIDE (R) (OR) HYDROPEROXIDE, 1-METHYL-1-PHENYLETHYL- (R)',
  },
  {
    code: 'U097',
    description: 'CARBAMIC CHLORIDE, DIMETHYL- (OR) DIMETHYLCARBAMOYL CHLORIDE',
  },
  {
    code: 'U098',
    description: '1,1-DIMETHYLHYDRAZINE (OR) HYDRAZINE, 1,1-DIMETHYL-',
  },
  {
    code: 'U099',
    description: '1,2-DIMETHYLHYDRAZINE (OR) HYDRAZINE, 1,2-DIPHENYL-',
  },
  {
    code: 'U101',
    description: '2,4-DIMETHYLPHENOL (OR) PHENOL, 2,4-DIMETHYL-',
  },
  {
    code: 'U102',
    description: '1,2-BENZENEDICARBOXYLIC ACID, DIMETHYL ESTER (OR) DIMETHYL PHTHALATE',
  },
  {
    code: 'U103',
    description: 'DIMETHYL SULFATE (OR) SULFURIC ACID, DIMETHYL ESTER',
  },
  {
    code: 'U105',
    description: '2,4-DINITROTOLUENE (OR) BENZENE, 1-METHYL-2,4-DINITRO-',
  },
  {
    code: 'U106',
    description: '2,6-DINITROTOLUENE (OR) BENZENE, 2-METHYL-1,3-DINITRO-',
  },
  {
    code: 'U107',
    description: '1,2-BENZENEDICARBOXYLIC ACID, DIOCTYL ESTER (OR) DI-N-OCTYL PHTHALATE',
  },
  {
    code: 'U108',
    description: '1,4-DIETHYLENEOXIDE (OR) 1,4-DIOXANE',
  },
  {
    code: 'U109',
    description: '1,2-DIPHENYLHYDRAZINE (OR) HYDRAZINE, 1,2-DIPHENYL-',
  },
  {
    code: 'U110',
    description: '1-PROPANIMINE, N-PROPYL-(I) (OR) DIPROPYLAMINE (I)',
  },
  {
    code: 'U111',
    description: '1-PROPANAMINE, N-NITROSO-N-PROPYL- (OR) DI-N-PROPYLNITROSAMINE',
  },
  {
    code: 'U112',
    description: 'ACETIC ACID, ETHYL ESTER (I) (OR) ETHYL ACETATE (I)',
  },
  {
    code: 'U113',
    description: '2-PROPENOIC ACID, ETHYL ESTER (I) (OR) ETHYL ACRYLATE (I)',
  },
  {
    code: 'U114',
    description:
      'CARBAMODITHIOIC ACID, 1,2-ETHANEDIYLBIS-, SALTS & ESTERS (OR) ETHYLENEBISDITHIOCARBAMIC ACID, SALTS & ESTERS',
  },
  {
    code: 'U115',
    description: 'ETHYLENE OXIDE (I,T) (OR) OXIRANE (I,T)',
  },
  {
    code: 'U116',
    description: '2-IMIDAZOLIDINETHIONE (OR) ETHYLENETHIOUREA',
  },
  {
    code: 'U117',
    description: "ETHANE, 1,1'-OXYBIS-(I) (OR) ETHYL ETHER (I)",
  },
  {
    code: 'U118',
    description: '2-PROPENOIC ACID, 2-METHYL-, ETHYL ESTER (OR) ETHYL METHACRYLATE',
  },
  {
    code: 'U119',
    description: 'ETHYL METHANESULFONATE (OR) METHANESULFONIC ACID, ETHYL ESTER',
  },
  {
    code: 'U120',
    description: 'FLUORANTHENE',
  },
  {
    code: 'U121',
    description: 'METHANE, TRICHLOROFLUORO- (OR) TRICHLOROMONOFLUOROMETHANE',
  },
  {
    code: 'U122',
    description: 'FORMALDEHYDE',
  },
  {
    code: 'U123',
    description: 'FORMIC ACID (C,T)',
  },
  {
    code: 'U124',
    description: 'FURAN (I) (OR) FURFURAN (I)',
  },
  {
    code: 'U125',
    description: '2-FURANCARBOXALDEHYDE (I) (OR) FURFURAL (I)',
  },
  {
    code: 'U126',
    description: 'GLYCIDYLALDEHYDE (OR) OXIRANECARBOXYALDEHYDE',
  },
  {
    code: 'U127',
    description: 'BENZENE, HEXACHLORO- (OR) HEXACHLOROBENZENE',
  },
  {
    code: 'U128',
    description: '1,3-BUTADIENE, 1,1,2,3,4,4-HEXACHLORO- (OR) HEXACHLOROBUTADIENE',
  },
  {
    code: 'U129',
    description: 'CYCLOHEXANE, 1,2,3,4,5,6-HEXACHLORO-, (1ALPHA, 2ALPHA, 3BETA, 4ALPHA, 5ALPHA, 6BETA)- (OR) LINDANE',
  },
  {
    code: 'U130',
    description: '1,3-CYCLOPENTADIENE, 1,2,3,4,5,5-HEXACHLORO- (OR) HEXACHLOROCYCLOPENTADIENE',
  },
  {
    code: 'U131',
    description: 'ETHANE, HEXACHLORO- (OR) HEXACHLOROETHANE',
  },
  {
    code: 'U132',
    description: "HEXACHLOROPHENE (OR) PHENOL, 2,2'-METHYLENEBIS[3,4,6-TRICHLORO-",
  },
  {
    code: 'U133',
    description: 'HYDRAZINE (R,T)',
  },
  {
    code: 'U134',
    description: 'HYDROFLUORIC ACID (C,T) (OR) HYDROGEN FLUORIDE (C,T)',
  },
  {
    code: 'U135',
    description: 'HYDROGEN SULFIDE (OR) HYDROGEN SULFIDE H2S',
  },
  {
    code: 'U136',
    description: 'ARSINIC ACID, DIMETHYL- (OR) CACODYLIC ACID',
  },
  {
    code: 'U137',
    description: 'INDENO[1,2,3-CD]PYRENE',
  },
  {
    code: 'U138',
    description: 'METHANE, IODO- (OR) METHYL IODIDE',
  },
  {
    code: 'U140',
    description: '1-PROPANOL, 2-METHYL- (I,T) (OR) ISOBUTYL ALCOHOL (I,T)',
  },
  {
    code: 'U141',
    description: '1,3-BENZODIOXOLE, 5-(1-PROPENYL)- (OR) ISOSAFROLE',
  },
  {
    code: 'U142',
    description:
      '1,3,4-METHENO-2H-CYCLOBUTA[CD]PENTALEN-2-ONE, 1,1A,3,3A,4,5,5,5A,5B,6-DECACHLOROOCTAHYDRO- (OR) KEPONE',
  },
  {
    code: 'U143',
    description:
      '2-BUTENOIC ACID, 2-METHYL-, 7-[[2,3-DIHYDROXY-2-(1-METHOXYETHYL)-3-METHYL-1-OXOBUTOXY]METHYL]-2,3,5,7A-TETRAHYDRO-1H-PYRROLIZIN-1-YL ESTER, [1S-[1ALPHA(Z), 7(2S*,3R*), 7AALPHA]]- (OR) LASIOCARPINE',
  },
  {
    code: 'U144',
    description: 'ACETIC ACID, LEAD(2+) SALT (OR) LEAD ACETATE',
  },
  {
    code: 'U145',
    description: 'LEAD PHOSPHATE (OR) PHOSPHORIC ACID, LEAD(2+) SALT (2:3)',
  },
  {
    code: 'U146',
    description: 'LEAD SUBACETATE (OR) LEAD, BIS(ACETATO-O)TETRAHYDROXYTRI-',
  },
  {
    code: 'U147',
    description: '2,5-FURANDIONE (OR) MALEIC ANHYDRIDE',
  },
  {
    code: 'U148',
    description: '3,6-PYRIDAZINEDIONE, 1,2-DIHYDRO- (OR) MALEIC HYDRAZIDE',
  },
  {
    code: 'U149',
    description: 'MALONONITRILE (OR) PROPANEDINITRILE',
  },
  {
    code: 'U150',
    description: 'L-PHENYLALANINE, 4-[BIS(2-CHLOROETHYL)AMINO]- (OR) MELPHALAN',
  },
  {
    code: 'U151',
    description: 'MERCURY',
  },
  {
    code: 'U152',
    description: '2-PROPENENITRILE, 2-METHYL- (I,T) (OR) METHACRYLONITRILE (I,T)',
  },
  {
    code: 'U153',
    description: 'METHANETHIOL (I,T) (OR) THIOMETHANOL (I,T)',
  },
  {
    code: 'U154',
    description: 'METHANOL (I) (OR) METHYL ALCOHOL (I)',
  },
  {
    code: 'U155',
    description: "1,2-ETHANEDIAMINE, N,N-DIMETHYL-N'-2-PYRIDINYL-N'-(2-THIENYLMETHYL)- (OR) METHAPYRILENE",
  },
  {
    code: 'U156',
    description: 'CARBONOCHLORIDIC ACID, METHYL ESTER, (I,T) (OR) METHYL CHLOROCARBONATE (I,T)',
  },
  {
    code: 'U157',
    description: '3-METHYLCHOLANTHRENE (OR) BENZ[J]ACEANTHRYLENE, 1,2-DIHYDRO-3-METHYL-',
  },
  {
    code: 'U158',
    description: "4,4'-METHYLENEBIS(2-CHLOROANILINE) (OR) BENZENAMINE, 4,4'-METHYLENEBIS[2-CHLORO-",
  },
  {
    code: 'U159',
    description: '2-BUTANONE (I,T) (OR) METHYL ETHYL KETONE (MEK) (I,T)',
  },
  {
    code: 'U160',
    description: '2-BUTANONE, PEROXIDE (R,T) (OR) METHYL ETHYL KETONE PEROXIDE (R,T)',
  },
  {
    code: 'U161',
    description: '4-METHYL-2-PENTANONE (I) (OR) METHYL ISOBUTYL KETONE (I) (OR) PENTANOL, 4-METHYL-',
  },
  {
    code: 'U162',
    description: '2-PROPENOIC ACID, 2-METHYL-, METHYL ESTER (I,T) (OR) METHYL METHACRYLATE (I,T)',
  },
  {
    code: 'U163',
    description: "GUANIDINE, N-METHYL-N'-NITRO-N-NITROSO- (OR) MNNG",
  },
  {
    code: 'U164',
    description: '4(1H)-PYRIMIDINONE, 2,3-DIHYDRO-6-METHYL-2-THIOXO- (OR) METHYLTHIOURACIL',
  },
  {
    code: 'U165',
    description: 'NAPHTHALENE',
  },
  {
    code: 'U166',
    description: '1,4-NAPHTHALENEDIONE (OR) 1,4-NAPHTHOQUINONE',
  },
  {
    code: 'U167',
    description: '1-NAPTHALENAMINE (OR) ALPHA-NAPHTHYLAMINE',
  },
  {
    code: 'U168',
    description: '2-NAPTHALENAMINE (OR) BETA-NAPHTHYLAMINE',
  },
  {
    code: 'U169',
    description: 'BENZENE, NITRO- (OR) NITROBENZENE (I,T)',
  },
  {
    code: 'U170',
    description: 'P-NITROPHENOL (I,T) (OR) PHENOL, 4-NITRO-',
  },
  {
    code: 'U171',
    description: '2-NITROPROPANE (I,T) (OR) PROPANE, 2-NITRO- (I,T)',
  },
  {
    code: 'U172',
    description: '1-BUTANAMINE, N-BUTYL-N-NITROSO- (OR) N-NITROSODI-N-BUTYLAMINE',
  },
  {
    code: 'U173',
    description: "ETHANOL, 2,2'-(NITROSOIMINO)BIS- (OR) N-NITROSODIETHANOLAMINE",
  },
  {
    code: 'U174',
    description: 'ETHANAMINE, N-ETHYL-N-NITROSO- (OR) N-NITROSODIETHYLAMINE',
  },
  {
    code: 'U176',
    description: 'N-NITROSO-N-ETHYLUREA (OR) UREA, N-ETHYL-N-NITROSO-',
  },
  {
    code: 'U177',
    description: 'N-NITROSO-N-METHYLUREA (OR) UREA, N-METHYL-N-NITROSO-',
  },
  {
    code: 'U178',
    description: 'CARBAMIC ACID, METHYLNITROSO-, ETHYL ESTER (OR) N-NITROSO-N-METHYLURETHANE',
  },
  {
    code: 'U179',
    description: 'N-NITROSOPIPERIDINE (OR) PIPERIDINE, 1-NITROSO-',
  },
  {
    code: 'U180',
    description: 'N-NITROSOPYRROLIDINE (OR) PYRROLIDINE, 1-NITROSO-',
  },
  {
    code: 'U181',
    description: '5-NITRO-O-TOLUIDINE (OR) BENZENAMINE, 2-METHYL-5-NITRO',
  },
  {
    code: 'U182',
    description: '1,3,5-TRIOXANE, 2,4,6-TRIMETHYL- (OR) PARALDEHYDE',
  },
  {
    code: 'U183',
    description: 'BENZENE, PENTACHLORO- (OR) PENTACHLOROBENZENE',
  },
  {
    code: 'U184',
    description: 'ETHANE, PENTACHLORO- (OR) PENTACHLOROETHANE',
  },
  {
    code: 'U185',
    description: 'BENZENE, PENTACHLORONITRO- (OR) PENTACHLORONITROBENZENE (PCNB)',
  },
  {
    code: 'U186',
    description: '1,3-PENTADIENE (I) (OR) 1-METHYLBUTADIENE (I)',
  },
  {
    code: 'U187',
    description: 'ACETAMIDE, N-(4-ETHOXYPHENYL)- (OR) PHENACETIN',
  },
  {
    code: 'U188',
    description: 'PHENOL',
  },
  {
    code: 'U189',
    description: 'PHOSPHORUS SULFIDE (R) (OR) SULFUR PHOSPHIDE (R)',
  },
  {
    code: 'U190',
    description: '1,3-ISOBENZOFURANDIONE (OR) PHTHALIC ANHYDRIDE',
  },
  {
    code: 'U191',
    description: '2-PICOLINE (OR) PYRIDINE, 2-METHYL-',
  },
  {
    code: 'U192',
    description: 'BENZAMIDE, 3,5-DICHLORO-N-(1,1-DIMETHYL-2-PROPYNYL)- (OR) PRONAMIDE',
  },
  {
    code: 'U193',
    description: '1,2-OXATHIOLANE, 2,2-DIOXIDE (OR) 1,3-PROPANE SULTONE',
  },
  {
    code: 'U194',
    description: '1-PROPANAMINE (I,T) (OR) N-PROPYLAMINE (I,T)',
  },
  {
    code: 'U196',
    description: 'PYRIDINE',
  },
  {
    code: 'U197',
    description: '2,5-CYCLOHEXADIENE-1,4-DIONE (OR) P-BENZOQUINONE',
  },
  {
    code: 'U200',
    description:
      'RESERPINE (OR) YOHIMBAN-16-CARBOXYLIC ACID, 11,17-DIMETHOXY-18-[(3,4,5-TRIMETHOXYBENZOYL)OXY]-, METHYL ESTER, (3BETA, 16BETA, 17ALPHA, 18BETA, 20ALPHA)-',
  },
  {
    code: 'U201',
    description: '1,3-BENZENEDIOL (OR) RESORCINOL',
  },
  {
    code: 'U203',
    description: '1,3-BENZODIOXOLE, 5-(2-PROPENYL)- (OR) SAFROLE',
  },
  {
    code: 'U204',
    description: 'SELENIOUS ACID (OR) SELENIUM DIOXIDE',
  },
  {
    code: 'U205',
    description: 'SELENIUM SULFIDE (OR) SELENIUM SULFIDE SES2 (R,T)',
  },
  {
    code: 'U206',
    description:
      'D-GLUCOSE, 2-DEOXY-2-[[(METHYLNITROSOAMINO)-CARBONYL]AMINO]- (OR) GLUCOPYRANOSE, 2-DEOXY-2-(3-METHYL-3-NITROSOUREIDO)-,D- (OR) STREPTOZOTOCIN',
  },
  {
    code: 'U207',
    description: '1,2,4,5-TETRACHLOROBENZENE (OR) BENZENE, 1,2,4,5-TETRACHLORO-',
  },
  {
    code: 'U208',
    description: '1,1,1,2-TETRACHLOROETHANE (OR) ETHANE, 1,1,1,2-TETRACHLORO-',
  },
  {
    code: 'U209',
    description: '1,1,2,2-TETRACHLOROETHANE (OR) ETHANE, 1,1,2,2-TETRACHLORO-',
  },
  {
    code: 'U210',
    description: 'ETHENE, TETRACHLORO- (OR) TETRACHLOROETHYLENE',
  },
  {
    code: 'U211',
    description: 'CARBON TETRACHLORIDE (OR) METHANE, TETRACHLORO-',
  },
  {
    code: 'U213',
    description: 'FURAN, TETRAHYDRO-(I) (OR) TETRAHYDROFURAN (I)',
  },
  {
    code: 'U214',
    description: 'ACETIC ACID, THALLIUM(1+) SALT (OR) THALLIUM(I) ACETATE',
  },
  {
    code: 'U215',
    description: 'CARBONIC ACID, DITHALLIUM(1+) SALT (OR) THALLIUM(I) CARBONATE',
  },
  {
    code: 'U216',
    description: 'THALLIUM CHLORIDE TLCL (OR) THALLIUM(I) CHLORIDE',
  },
  {
    code: 'U217',
    description: 'NITRIC ACID, THALLIUM(1+) SALT (OR) THALLIUM(I) NITRATE',
  },
  {
    code: 'U218',
    description: 'ETHANETHIOAMIDE (OR) THIOACETAMIDE',
  },
  {
    code: 'U219',
    description: 'THIOUREA',
  },
  {
    code: 'U220',
    description: 'BENZENE, METHYL- (OR) TOLUENE',
  },
  {
    code: 'U221',
    description: 'BENZENEDIAMINE, AR-METHYL- (OR) TOLUENEDIAMINE',
  },
  {
    code: 'U222',
    description: 'BENZENAMINE, 2-METHYL-, HYDROCHLORIDE (OR) O-TOLUIDINE HYDROCHLORIDE',
  },
  {
    code: 'U223',
    description: 'BENZENE, 1,3-DIISOCYANATOMETHYL- (R,T) (OR) TOLUENE DIISOCYANATE (R,T)',
  },
  {
    code: 'U225',
    description: 'BROMOFORM (OR) METHANE, TRIBROMO-',
  },
  {
    code: 'U226',
    description: 'ETHANE, 1,1,1-TRICHLORO- (OR) METHYL CHLOROFORM',
  },
  {
    code: 'U227',
    description: '1,1,2-TRICHLOROETHANE (OR) ETHANE, 1,1,2-TRICHLORO-',
  },
  {
    code: 'U228',
    description: 'ETHENE, TRICHLORO- (OR) TRICHLOROETHYLENE',
  },
  {
    code: 'U234',
    description: '1,3,5-TRINITROBENZENE (R,T) (OR) BENZENE, 1,3,5-TRINITRO-',
  },
  {
    code: 'U235',
    description: '1-PROPANOL, 2,3-DIBROMO-, PHOSPHATE (3:1) (OR) TRIS(2,3,-DIBROMOPROPYL) PHOSPHATE',
  },
  {
    code: 'U236',
    description:
      "2,7-NAPHTHALENEDISULFONIC ACID,3,3'-[(3,3'-DIMETHYL[1,1'-BIPHENYL]-4,4'-DIYL)BIS(AZO)BIS[5-AMINO-4-HYDROXY]-, TETRASODIUM SALT (OR) TRYPAN BLUE",
  },
  {
    code: 'U237',
    description: '2,4-(1H,3H)-PYRIMIDINEDIONE, 5-[BIS(2-CHLOROETHYL)AMINO]- (OR) URACIL MUSTARD',
  },
  {
    code: 'U238',
    description: 'CARBAMIC ACID, ETHYL ESTER (OR) ETHYL CARBAMATE (URETHANE)',
  },
  {
    code: 'U239',
    description: 'BENZENE, DIMETHYL- (I,T) (OR) XYLENE (I)',
  },
  {
    code: 'U240',
    description:
      '2,4-D, SALTS & ESTERS (OR) ACETIC ACID, (2,4-DICHLOROPHENOXY)-, SALTS & ESTERS (OR) DICHLOROPHENOXYACETIC ACID 2,4-D',
  },
  {
    code: 'U243',
    description: '1-PROPENE, 1,1,2,3,3,3-HEXACHLORO- (OR) HEXACHLOROPROPENE',
  },
  {
    code: 'U244',
    description: 'THIOPEROXYDICARBONIC DIAMIDE [(H2N)C(S)]2S2, TETRAMETHYL- (OR) THIRAM',
  },
  {
    code: 'U246',
    description: 'CYANOGEN BROMIDE (CN)BR',
  },
  {
    code: 'U247',
    description: "BENZENE, 1,1'-(2,2,2-TRICHLOROETHYLIDENE)BIS[4-METHOXY- (OR) METHOXYCHLOR",
  },
  {
    code: 'U248',
    description:
      '2H-1-BENZOPYRAN-2-ONE, 4-HYDROXY-3-(3-OXO-1-PHENYL-BUTYL)-, & SALTS, WHEN PRESENT AT CONCENTRATIONS OF 0.3% OR LESS (OR) WARFARIN, & SALTS, WHEN PRESENT AT CONCENTRATIONS OF 0.3% OR LESS',
  },
  {
    code: 'U249',
    description: 'ZINC PHOSPHIDE ZN3P2, WHEN PRESENT AT CONCENTRATIONS OF 10% OR LESS',
  },
  {
    code: 'U271',
    description: 'BENOMYL (OR) CARBAMIC ACID, [1-[(BUTYLAMINO)CARBONYL]-1H-BENZIMIDAZOL-2-YL]-, METHYL ESTER',
  },
  {
    code: 'U278',
    description: 'BENDIOCARB (OR) 1,3-BENZODIOXOL-4-OL, 2,2-DIMETHYL-, METHYL CARBAMATE',
  },
  {
    code: 'U279',
    description: 'CARBARYL (OR) 1-NAPHTHALENOL, METHYLCARBAMATE',
  },
  {
    code: 'U280',
    description: 'BARBAN (OR) CARBAMIC ACID, (3-CHLOROPHENYL)-, 4-CHLORO-2-BUTYNYL ESTER',
  },
  {
    code: 'U328',
    description: 'BENZENAMINE, 2-METHYL- (OR) O-TOLUIDINE',
  },
  {
    code: 'U353',
    description: 'BENZENAMINE, 4-METHYL- (OR) P-TOLUIDINE',
  },
  {
    code: 'U359',
    description: 'ETHANOL, 2-ETHOXY- (OR) ETHYLENE GLYCOL MONOETHYL ETHER',
  },
  {
    code: 'U364',
    description: 'BENDIOCARB PHENOL (OR) 1,3-BENZODIOXOL-4-OL, 2,2-DIMETHYL-',
  },
  {
    code: 'U367',
    description: '7-BENZOFURANOL, 2,3-DIHYDRO-2,2-DIMETHYL- (OR) CARBOFURAN PHENOL',
  },
  {
    code: 'U372',
    description: 'CARBAMIC ACID, 1H-BENZIMIDAZOL-2-YL, METHYL ESTER (OR) CARBENDAZIM',
  },
  {
    code: 'U373',
    description: 'CARBAMIC ACID, PHENYL-, 1-METHYLETHYL ESTER (OR) PROPHAM',
  },
  {
    code: 'U378',
    description:
      'CARBAMODITHIOIC ACID, (HYDROXYMETHYL) METHYL-, MONOPOTASSIUM SALT (OR) POTASSIUM N-HYDROXYMETHYL- N-METHYLDI-THIOCARBAMATE',
  },
  {
    code: 'U387',
    description: 'CARBAMOTHIOIC ACID, DIPROPYL-, S-(PHENYLMETHYL) ESTER (OR) PROSULFOCARB',
  },
  {
    code: 'U389',
    description: 'CARBAMOTHIOIC ACID, BIS(1-METHYLETHYL)-, S-(2,3,3-TRICHLORO-2-PROPENYL) ESTER (OR) TRIALLATE',
  },
  {
    code: 'U394',
    description: 'A2213 (OR) ETHANIMIDOTHIOIC ACID, 2-(DIMETHYLAMINO)-N-HYDROXY-2-OXO-, METHYL ESTER',
  },
  {
    code: 'U395',
    description: "DIETHYLENE GLYCOL, DICARBAMATE (OR) ETHANOL, 2,2'-OXYBIS-, DICARBAMATE",
  },
  {
    code: 'U404',
    description: 'ETHANAMINE, N,N-DIETHYL- (OR) TRIETHYLAMINE',
  },
  {
    code: 'U409',
    description: 'CARBAMIC ACID, [1,2-PHENYLENEBIS (IMINOCARBONOTHIOYL)]BIS-, DIMETHYL ESTER (OR) THIOPHANATE-METHYL',
  },
  {
    code: 'U410',
    description: "ETHANIMIDOTHIOIC ACID, N,N'-[THIOBIS[(METHYLIMINO)CARBONYLOXY]]BIS-, DIMETHYL ESTER (OR) THIODICARB",
  },
  {
    code: 'U411',
    description: 'PHENOL, 2-(1-METHYLETHOXY)-, METHYLCARBAMATE (OR) PROPOXUR',
  },
];
